import { useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { formatIDR, formatDateID } from "@/lib/format";
import { toast } from "sonner";
import { Area, AreaChart, ResponsiveContainer } from "recharts";
import {
  LayoutDashboard, ShoppingCart, Wallet, Package, Layers, Users, Tag, Truck, Database, ClipboardCheck,
  UserCog, Fingerprint, CalendarDays, FileClock, Clock4, Banknote, HandCoins, CheckSquare, BadgeDollarSign,
  Megaphone, ShieldAlert, Store, BarChart3, Settings, ScrollText, LogOut, Menu, X, ChevronRight, ChevronDown, Plus, Power, Check, Star, MessageCircle,
  ClipboardList, RotateCcw, Printer, Search, ShoppingBag, FileText, Pencil, MoreVertical, Download, Upload, Sparkles, Factory, Inbox, ScanBarcode,
} from "lucide-react";

const MENU = [
  { section: "Utama", items: [
    { to: "/admin", end: true, icon: LayoutDashboard, label: "Dashboard", testid: "admin-nav-dash" },
  ]},
  { section: "Pesanan", items: [
    { to: "/admin/orders", icon: ClipboardList, label: "Pesanan Saya", testid: "admin-nav-orders" },
    { to: "/admin/returns", icon: RotateCcw, label: "Pengembalian/Pembatalan", testid: "admin-nav-returns" },
    { to: "/admin/shipping/settings", icon: Truck, label: "Pengaturan Pengiriman", testid: "admin-nav-shipset" },
  ]},
  { section: "Penjualan", items: [
    { to: "/admin/payments", icon: Wallet, label: "Pembayaran Manual", testid: "admin-nav-payments" },
    { to: "/admin/chat", icon: MessageCircle, label: "Chat Pelanggan", testid: "admin-nav-chat" },
  ]},
  { section: "Point of Sale", items: [
    { to: "/admin/pos/dashboard", icon: LayoutDashboard, label: "Dashboard", testid: "admin-nav-pos-dash" },
    { to: "/admin/pos/sales", icon: ShoppingBag, label: "Penjualan Produk", testid: "admin-nav-pos-sales" },
    { to: "/admin/pos/reports", icon: FileText, label: "Laporan Penjualan", testid: "admin-nav-pos-reports" },
    { to: "/admin/pos/customers", icon: Users, label: "Data Pelanggan", testid: "admin-nav-pos-cust" },
    { to: "/admin/pos/barcode", icon: ScanBarcode, label: "Cetak Barcode Produk", testid: "admin-nav-pos-barcode" },
  ]},
  { section: "Produk", items: [
    { to: "/admin/products", icon: Package, label: "Produk", testid: "admin-nav-products" },
    { to: "/admin/categories", icon: Layers, label: "Kategori", testid: "admin-nav-cat" },
    { to: "/admin/vouchers", icon: Tag, label: "Voucher & Promo", testid: "admin-nav-voucher" },
    { to: "/admin/reviews", icon: Star, label: "Penilaian Produk", testid: "admin-nav-reviews" },
    { to: "/admin/suppliers", icon: Truck, label: "Supplier & Pembelian", testid: "admin-nav-sup" },
  ]},
  { section: "Human Resources", items: [
    { to: "/admin/employees", icon: UserCog, label: "Karyawan", testid: "admin-nav-emp" },
    { to: "/admin/appraisals", icon: ClipboardCheck, label: "Penilaian", testid: "admin-nav-appr" },
    { to: "/admin/attendance", icon: Fingerprint, label: "Absensi", testid: "admin-nav-att" },
    { to: "/admin/schedule", icon: CalendarDays, label: "Shift Kerja", testid: "admin-nav-cal" },
    { to: "/admin/leave", icon: FileClock, label: "Izin Karyawan", testid: "admin-nav-leave" },
    { to: "/admin/overtime", icon: Clock4, label: "Lembur", testid: "admin-nav-ovt" },
    { to: "/admin/payroll", icon: Banknote, label: "Payroll", testid: "admin-nav-pay" },
    { to: "/admin/kasbon", icon: HandCoins, label: "Kasbon", testid: "admin-nav-kas" },
    { to: "/admin/remuneration", icon: BadgeDollarSign, label: "Remunisasi", testid: "admin-nav-rem" },
    { to: "/admin/tasks", icon: CheckSquare, label: "Tugas / SPK", testid: "admin-nav-task" },
    { to: "/admin/announcements", icon: Megaphone, label: "Pengumuman", testid: "admin-nav-ann" },
    { to: "/admin/violations", icon: ShieldAlert, label: "Surat Peringatan", testid: "admin-nav-vio" },
  ]},
  { section: "Produksi", items: [
    { to: "/admin/production", icon: Factory, label: "Produksi", testid: "admin-nav-prod" },
    { to: "/admin/production/submissions", icon: Inbox, label: "Pengajuan Pengawas", testid: "admin-nav-prod-subs", badge: "prod" },
    { to: "/admin/production/costs", icon: ShoppingBag, label: "Produk Produksi", testid: "admin-nav-prod-costs" },
    { to: "/admin/production/master", icon: Database, label: "Master Produksi", testid: "admin-nav-prod-master" },
    { to: "/admin/production/wages", icon: BadgeDollarSign, label: "Payout Produksi", testid: "admin-nav-prod-wages" },
  ]},
  { section: "Sistem", items: [
    { to: "/admin/reports", icon: BarChart3, label: "Laporan", testid: "admin-nav-rep" },
    { to: "/admin/settings", icon: Settings, label: "Pengaturan", testid: "admin-nav-set" },
    { to: "/admin/settings/ai", icon: Sparkles, label: "AI & Aturan Harga", testid: "admin-nav-ai" },
    { to: "/admin/settings/master", icon: Database, label: "Master Data", testid: "admin-nav-master" },
    { to: "/admin/audit", icon: ScrollText, label: "Audit Log", testid: "admin-nav-audit" },
  ]},
];

export default function AdminLayout() {
  const { user, logout, hasPerm } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [collapsed, setCollapsed] = useState({});
  const [prodBadge, setProdBadge] = useState(0);

  useEffect(() => {
    if (!hasPerm?.("production.view")) return;
    const load = () => api.get("/prod/submissions/pending-count").then((r) => setProdBadge(r.data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen admin-canvas flex">
      {/* Sidebar */}
      <aside className={`admin-sidebar text-slate-600 fixed lg:static inset-y-0 left-0 w-64 z-40 flex-shrink-0 flex flex-col transition-transform ${open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="px-4 py-4 flex items-center gap-2.5 border-b border-slate-100">
          <div className="w-9 h-9 rounded-full bg-brand flex items-center justify-center flex-shrink-0">
            <span className="text-white font-serif-fashion">K</span>
          </div>
          <div className="min-w-0">
            <div className="font-display font-bold text-slate-900 leading-tight text-sm">Kiyana Fashion ID</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-400">Admin Suite</div>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-3 px-2.5 text-sm">
          {MENU.map((sec) => {
            const isCollapsed = !!collapsed[sec.section];
            return (
              <div key={sec.section} className="mb-1">
                {sec.section !== "Utama" && (
                  <button
                    data-testid={`admin-nav-group-${sec.section.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setCollapsed((c) => ({ ...c, [sec.section]: !c[sec.section] }))}
                    className="w-full flex items-center justify-between px-2.5 py-2 text-[13px] font-medium text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span>{sec.section}</span>
                    <ChevronDown className={`w-4 h-4 transition-transform ${isCollapsed ? "-rotate-90" : ""}`} />
                  </button>
                )}
                {!isCollapsed && sec.items.map((it) => (
                  <NavLink
                    key={it.to}
                    to={it.to}
                    end={it.end}
                    data-testid={it.testid}
                    onClick={() => setOpen(false)}
                    className={({ isActive }) =>
                      `flex items-center gap-2.5 px-3 py-2 rounded-lg mb-0.5 transition-colors ${isActive ? "bg-violet-50 text-violet-700 font-semibold" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`
                    }
                  >
                    <it.icon className={`w-4 h-4 flex-shrink-0`} />
                    <span className="truncate">{it.label}</span>
                    {it.badge === "prod" && prodBadge > 0 && (
                      <span data-testid="admin-nav-prod-badge" className="ml-auto min-w-5 h-5 px-1 rounded-full bg-[#7F1D2D] text-white text-[10px] font-bold flex items-center justify-center">{prodBadge}</span>
                    )}
                  </NavLink>
                ))}
              </div>
            );
          })}
        </div>
        <div className="p-3 border-t border-slate-100">
          <div className="text-xs text-slate-500 mb-2 px-1 truncate">{user?.name} • {user?.role_label}</div>
          <button data-testid="admin-logout-btn" onClick={() => { logout(); nav("/login"); }} className="w-full h-9 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm flex items-center justify-center gap-2 transition-colors">
            <LogOut className="w-4 h-4" /> Keluar
          </button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-slate-200 px-4 py-3 flex items-center justify-between lg:justify-end">
          <button className="lg:hidden text-slate-700" onClick={() => setOpen(!open)}>
            {open ? <X /> : <Menu />}
          </button>
          <div className="text-xs text-slate-500 flex items-center gap-2">
            <span>{new Date().toLocaleDateString("id-ID", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}</span>
            <span className="hidden sm:inline">•</span>
            <span className="hidden sm:inline">WIB</span>
          </div>
        </header>
        <main className="flex-1 p-4 md:p-6 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function Sparkline({ id, color, data }) {
  return (
    <div className="h-10 mt-3 -mx-4 -mb-4" data-testid={`${id}-chart`}>
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
          <defs>
            <linearGradient id={id} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.22} />
              <stop offset="100%" stopColor={color} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="v" stroke={color} strokeWidth={2} fill={`url(#${id})`} isAnimationActive={false} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

const wave = (seed) => [...Array(12)].map((_, i) => ({ i, v: 3 + Math.sin(i * 0.9 + seed) * 2 + Math.cos(i * 0.5 + seed * 2) }));

export function AdminDashboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [orders, setOrders] = useState([]);
  const [audit, setAudit] = useState([]);
  useEffect(() => {
    api.get("/dashboard/admin").then((r) => setData(r.data));
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(()=>{});
    api.get("/audit-logs").then((r) => setAudit(r.data)).catch(()=>{});
  }, []);

  const omzetTrend = useMemo(() => {
    const days = [...Array(7)].map((_, i) => {
      const d = new Date();
      d.setDate(d.getDate() - (6 - i));
      return d.toDateString();
    });
    const vals = days.map(() => 0);
    orders.forEach((o) => {
      const idx = days.indexOf(new Date(o.created_at).toDateString());
      if (idx >= 0 && o.payment_status === "sudah_dibayar") vals[idx] += o.total;
    });
    return vals.map((v, i) => ({ i, v }));
  }, [orders]);

  const stats = [
    { label: "Omzet Total", value: data ? formatIDR(data.omzet_total) : "…", sub: "Akumulasi pesanan lunas", color: "#7C3AED", spark: omzetTrend },
    { label: "Pesanan Web", value: data?.orders_count ?? "…", sub: "Total pesanan masuk", color: "#0EA5E9", spark: wave(1) },
    { label: "Transaksi POS", value: data?.pos_transactions ?? "…", sub: "Penjualan toko fisik", color: "#10B981", spark: wave(2) },
    { label: "Menunggu Verifikasi", value: data?.pending_manual_payments ?? "…", sub: "Bukti transfer perlu dicek", color: "#F59E0B", spark: wave(3) },
    { label: "Stok Menipis", value: data?.low_stock_products ?? "…", sub: "Produk perlu restock", color: "#EF4444", spark: wave(4) },
    { label: "Karyawan Hadir", value: `${data?.hadir_today ?? 0} / ${data?.employees_total ?? 0}`, sub: "Kehadiran hari ini", color: "#8B5CF6", spark: wave(5) },
    { label: "COD Ditolak Pembeli", value: data ? `${data.cod_refused}/${data.cod_total}` : "…", sub: data ? `${data.cod_refusal_rate}% tingkat penolakan COD` : "…", color: "#EF4444", spark: wave(6) },
    { label: "Akun COD Diblokir", value: data?.cod_blocked_customers ?? "…", sub: "Tidak bisa checkout COD", color: "#F97316", spark: wave(7) },
  ];

  return (
    <div className="space-y-5">
      <div>
        <h1 data-testid="dashboard-title" className="font-display text-2xl md:text-3xl font-bold text-slate-900">Dashboard {user?.role === "owner" ? "Owner" : user?.role_label || ""}</h1>
        <p className="text-sm text-slate-500">Ringkasan operasional Kiyana hari ini.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {stats.map((s, i) => (
          <div key={i} data-testid={`stat-card-${i}`} className="bg-white rounded-2xl border border-slate-200 p-4 overflow-hidden">
            <div className="text-xs text-slate-500">{s.label}</div>
            <div className="text-2xl font-display font-bold mt-1 text-slate-900">{s.value}</div>
            <div className="text-[11px] mt-0.5" style={{ color: s.color }}>{s.sub}</div>
            <Sparkline id={`spark-${i}`} color={s.color} data={s.spark} />
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-2xl border border-slate-200">
          <div className="p-4 border-b border-slate-100 font-semibold text-slate-900">Pesanan Terbaru</div>
          <div className="divide-y divide-slate-100">
            {orders.slice(0, 6).map((o) => (
              <div key={o.id} className="p-3.5 flex items-center justify-between text-sm">
                <div>
                  <div className="font-semibold text-slate-900">#{o.code}</div>
                  <div className="text-xs text-slate-500">{o.customer_name} • {formatDateID(o.created_at)}</div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-kiyana-maroon">{formatIDR(o.total)}</div>
                  <div className="text-[10px] text-slate-500">{o.payment_status?.replace(/_/g, " ")}</div>
                </div>
              </div>
            ))}
            {orders.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Belum ada pesanan.</div>}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200">
          <div className="p-4 border-b border-slate-100 font-semibold text-slate-900">Aktivitas Terbaru</div>
          <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
            {audit.slice(0, 12).map((a) => (
              <div key={a.id} className="p-3 text-xs">
                <div className="font-semibold text-slate-800">{a.user_email} • {a.action}</div>
                <div className="text-slate-500">{a.module} • {formatDateID(a.created_at)}</div>
              </div>
            ))}
            {audit.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Belum ada aktivitas.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

const ADMIN_TABS = [
  { id: "semua", name: "Semua" },
  { id: "belum_bayar", name: "Menunggu Konfirmasi Pembayaran" },
  { id: "perlu_diproses", name: "Menunggu Diproses" },
  { id: "diproses", name: "Siap Kirim" },
  { id: "dikirim", name: "Dikirim" },
  { id: "selesai", name: "Selesai" },
  { id: "batal_retur", name: "Pembatalan/Retur" },
];

const UNPAID_PS = ["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit", "bukti_ditolak", "menunggu_verifikasi_manual", "cod_menunggu_verifikasi", "cod_ditolak"];
const PAID_PS = ["sudah_dibayar", "cod_belum_terbayar"];
const PAY_SHORT = { manual: "Transfer/QRIS", manual_transfer: "Transfer Bank", manual_qris: "QRIS", xendit_va: "Virtual Account", cod: "COD" };

const adminMatchTab = (o, tab) => {
  const ps = o.payment_status, os = o.order_status || "";
  if (tab === "belum_bayar") return UNPAID_PS.includes(ps) && os !== "dibatalkan";
  if (tab === "perlu_diproses") return PAID_PS.includes(ps) && ["pembayaran_diverifikasi", "perlu_diproses"].includes(os);
  if (tab === "diproses") return PAID_PS.includes(ps) && ["diproses", "dikemas", "siap_dikirim"].includes(os);
  if (tab === "dikirim") return os === "dikirim";
  if (tab === "selesai") return ["diterima", "selesai"].includes(os);
  if (tab === "batal_retur") return os === "dibatalkan" || os.startsWith("retur") || os.startsWith("refund") || o.cancel_request?.status === "menunggu_persetujuan";
  return true;
};

export function AdminOrders({ preset } = {}) {
  const [orders, setOrders] = useState([]);
  const [open, setOpen] = useState(null);
  const [ship, setShip] = useState({ awb: "" });
  const [selected, setSelected] = useState({});
  const [resiMap, setResiMap] = useState({});
  const [trackOpen, setTrackOpen] = useState(null);
  const [trackData, setTrackData] = useState({});
  const [trackLoading, setTrackLoading] = useState(null);
  const [payRejectId, setPayRejectId] = useState(null);
  const [payReason, setPayReason] = useState("");
  const [msg, setMsg] = useState("");
  const [tab, setTab] = useState(preset === "returns" ? "batal_retur" : "semua");
  const [q, setQ] = useState("");
  const [expandItems, setExpandItems] = useState({});
  const [returRejectId, setReturRejectId] = useState(null);
  const [returReason, setReturReason] = useState("");
  const [cancelRejectId, setCancelRejectId] = useState(null);
  const [cancelNote, setCancelNote] = useState("");
  const [codRejectId, setCodRejectId] = useState(null);
  const [codNote, setCodNote] = useState("");
  const load = () => api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      const r = await api.patch(`/admin/orders/${id}/status`, { order_status: status });
      const d = r.data || {};
      setMsg(d.auto_booked
        ? `Resi otomatis dibuat via Biteship: ${d.awb} — pesanan langsung Dikirim`
        : d.book_error
          ? `Pesanan Siap Kirim — resi otomatis gagal (${d.book_error}), silakan input resi manual`
          : "Status pesanan diperbarui");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memperbarui status"); }
  };

  const saveShipment = async (id) => {
    if (!ship.awb.trim()) return setMsg("Isi nomor resi dulu");
    try {
      await api.patch(`/admin/orders/${id}/shipment`, { awb: ship.awb });
      setMsg("Resi tersimpan — status menjadi Dikirim");
      setShip({ awb: "" });
      setOpen(null);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan resi"); }
  };

  const bulkProcess = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    try {
      const r = await api.post("/admin/orders/bulk-process", { order_ids: ids });
      setMsg(`${r.data.processed} pesanan diproses → Siap Kirim${r.data.auto_booked ? ` • ${r.data.auto_booked} resi otomatis via Biteship` : ""}${r.data.skipped ? ` (${r.data.skipped} dilewati)` : ""}`);
      setSelected({});
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memproses massal"); }
  };

  const bulkApprovePayments = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    let ok = 0, fail = 0;
    for (const id of ids) {
      try { await api.post(`/admin/orders/${id}/payment-review`, { action: "terima" }); ok++; }
      catch { fail++; }
    }
    setMsg(`${ok} pembayaran diterima → Menunggu Diproses${fail ? ` (${fail} gagal)` : ""}`);
    setSelected({});
    load();
  };

  const payAct = async (id, action) => {
    try {
      await api.post(`/admin/orders/${id}/payment-review`, { action, reason: payReason });
      setMsg(action === "terima" ? "Pembayaran diterima — pesanan masuk Menunggu Diproses" : "Pembayaran ditolak");
      setPayRejectId(null);
      setPayReason("");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memproses pembayaran"); }
  };

  const saveResiInline = async (o) => {
    const awb = (resiMap[o.id] || "").trim();
    if (awb.length < 4) return setMsg("Nomor resi tidak valid (min. 4 karakter)");
    try {
      await api.patch(`/admin/orders/${o.id}/shipment`, { awb });
      setMsg(`Resi ${o.code} tersimpan — status menjadi Dikirim`);
      setResiMap((m) => ({ ...m, [o.id]: "" }));
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan resi"); }
  };

  const toggleTrack = async (o) => {
    if (trackOpen === o.id) return setTrackOpen(null);
    setTrackOpen(o.id);
    if (trackData[o.id]) return;
    setTrackLoading(o.id);
    try {
      const si = o.shipping_info || {};
      const r = await api.post("/shipping/track", { courier: (si.courier_code || si.courier || "").toLowerCase(), awb: si.awb });
      setTrackData((m) => ({ ...m, [o.id]: r.data }));
    } catch {
      setTrackData((m) => ({ ...m, [o.id]: { error: true } }));
    } finally {
      setTrackLoading(null);
    }
  };

  const returAct = async (id, action) => {
    try {
      await api.patch(`/admin/orders/${id}/retur`, { action, reason: returReason });
      setMsg("Status retur diperbarui");
      setReturRejectId(null);
      setReturReason("");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memproses retur"); }
  };

  const cancelAct = async (id, action) => {
    try {
      const r = await api.post(`/admin/orders/${id}/cancel-review`, { action, note: cancelNote });
      const b = r.data || {};
      setMsg(action === "setuju"
        ? `Pembatalan disetujui — pesanan dibatalkan, refund diproses manual${b.biteship_cancelled ? " • booking Biteship ikut dibatalkan" : b.biteship_error ? ` • cancel Biteship gagal: ${b.biteship_error}` : ""}`
        : "Pengajuan pembatalan ditolak");
      setCancelRejectId(null);
      setCancelNote("");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memproses pembatalan"); }
  };

  const codAct = async (id, action) => {
    try {
      const r = await api.post(`/admin/orders/${id}/cod-review`, { action, note: codNote });
      const b = r.data || {};
      const baseMsg = action === "setuju" ? "COD disetujui — pesanan masuk antrean Menunggu Diproses" : action === "tolak" ? "Pengajuan COD ditolak — pesanan dibatalkan" : action === "ditolak_pembeli" ? "Paket COD ditolak pembeli — akun diblokir dari COD" : "Pembayaran COD ditandai lunas";
      setMsg(baseMsg + (b.biteship_cancelled ? " • booking Biteship ikut dibatalkan" : b.biteship_error ? ` • cancel Biteship gagal: ${b.biteship_error}` : ""));
      setCodRejectId(null);
      setCodNote("");
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal memproses COD"); }
  };

  const counts = {};
  ADMIN_TABS.forEach((t) => { counts[t.id] = orders.filter((o) => adminMatchTab(o, t.id)).length; });
  const qlc = q.trim().toLowerCase();
  const filtered = orders
    .filter((o) => adminMatchTab(o, tab))
    .filter((o) => !qlc || (o.code || "").toLowerCase().includes(qlc) || (o.customer_name || "").toLowerCase().includes(qlc));
  const isReturns = preset === "returns";
  const isProcessable = (o) => PAID_PS.includes(o.payment_status) && ["pembayaran_diverifikasi", "perlu_diproses"].includes(o.order_status || "");
  const isSelectable = (o) => (tab === "belum_bayar" ? o.payment_status === "menunggu_verifikasi_manual" : isProcessable(o));
  const selectable = filtered.filter(isSelectable);
  const selCount = Object.values(selected).filter(Boolean).length;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold">{isReturns ? "Pengembalian / Pembatalan" : "Pesanan Saya"}</h1>
        <p className="text-sm text-slate-500">{isReturns ? "Kelola pengajuan retur, refund, dan pembatalan pelanggan." : "Alur: Pembayaran ACC → Menunggu Diproses (cetak label A6) → Siap Kirim (input resi) → Dikirim (tracking) → Selesai."}</p>
        {msg && <div className="text-xs text-kiyana-maroon mt-1" data-testid="orders-msg">{msg}</div>}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {!isReturns && ADMIN_TABS.map((t) => (
          <button key={t.id} data-testid={`orders-tab-${t.id}`} onClick={() => { setTab(t.id); setSelected({}); }}
            className={`h-9 px-4 rounded-full text-xs font-semibold border transition ${tab === t.id ? "bg-violet-600 text-white border-violet-600" : "bg-white text-slate-600 border-slate-200 hover:border-violet-300"}`}>
            {t.name} <span className="opacity-70">({counts[t.id]})</span>
          </button>
        ))}
        <div className={`relative ${isReturns ? "w-full md:w-96" : "md:ml-auto"}`}>
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="orders-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari no. pesanan / nama pelanggan"
            className="h-9 w-full md:w-72 pl-9 pr-3 rounded-full border border-slate-200 bg-white text-xs" />
        </div>
      </div>

      {selectable.length > 0 && (
        <div className="bg-white rounded-xl border border-slate-200 px-4 py-2.5 flex items-center gap-3 flex-wrap">
          <label className="flex items-center gap-2 text-xs font-semibold text-slate-700 cursor-pointer">
            <input type="checkbox" data-testid="orders-select-all" className="w-4 h-4 accent-violet-600"
              checked={selectable.every((o) => selected[o.id])}
              onChange={(e) => { const on = e.target.checked; const s = { ...selected }; selectable.forEach((o) => { s[o.id] = on; }); setSelected(s); }} />
            Pilih Semua ({selectable.length})
          </label>
          <span className="text-xs text-slate-400" data-testid="orders-selected-count">{selCount} dipilih</span>
          {tab === "belum_bayar" ? (
            <button data-testid="bulk-pay-btn" onClick={bulkApprovePayments} disabled={!selCount}
              className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold disabled:opacity-40 ml-auto transition-colors">
              Terima Pembayaran ({selCount})
            </button>
          ) : (
            <button data-testid="bulk-proses-btn" onClick={bulkProcess} disabled={!selCount}
              className="h-9 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold disabled:opacity-40 ml-auto transition-colors">
              Proses Sekaligus → Siap Kirim
            </button>
          )}
        </div>
      )}

      {filtered.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-500 text-sm">Tidak ada pesanan pada filter ini.</div>}

      {filtered.map((o) => {
        const itemsExpanded = !!expandItems[o.id];
        const shownItems = itemsExpanded ? (o.items || []) : (o.items || []).slice(0, 1);
        const os = o.order_status || "";
        const needProcess = PAID_PS.includes(o.payment_status) && ["pembayaran_diverifikasi", "perlu_diproses"].includes(os);
        const inPrep = PAID_PS.includes(o.payment_status) && ["diproses", "dikemas", "siap_dikirim"].includes(os);
        return (
          <div key={o.id} className="bg-white rounded-xl border border-slate-200" data-testid={`order-card-${o.code}`}>
            <div className="flex items-center justify-between gap-2 px-4 pt-3 text-xs flex-wrap">
              <div className="font-semibold text-slate-800 flex items-center gap-1.5"><Users className="w-3.5 h-3.5 text-slate-400" />{o.customer_name}</div>
              <div className="text-slate-500">No. Pesanan <span className="font-mono font-semibold text-slate-700">{o.code}</span> • {formatDateID(o.created_at)}</div>
            </div>
            <div className="px-4 py-3 flex flex-wrap gap-4">
              {isSelectable(o) && (
                <div className="flex items-center">
                  <input type="checkbox" data-testid={`order-check-${o.code}`} className="w-4 h-4 accent-violet-600"
                    checked={!!selected[o.id]} onChange={(e) => setSelected((s) => ({ ...s, [o.id]: e.target.checked }))} />
                </div>
              )}
              <div className="flex-1 min-w-[260px]">
                {shownItems.map((it, i) => (
                  <div key={i} className="flex gap-3 items-start mb-2 last:mb-0">
                    <img src={it.image} alt="" className="w-14 h-14 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                    <div className="min-w-0">
                      <div className="text-sm font-semibold line-clamp-2">{it.name}</div>
                      <div className="text-xs text-slate-500">Variasi: {it.variant || "-"}</div>
                      <div className="text-xs text-slate-500">x{it.qty} • {formatIDR(it.price)}</div>
                    </div>
                  </div>
                ))}
                {(o.items || []).length > 1 && (
                  <button data-testid={`expand-items-${o.code}`} onClick={() => setExpandItems((s) => ({ ...s, [o.id]: !itemsExpanded }))}
                    className="mt-1 text-xs text-violet-700 font-semibold flex items-center gap-1">
                    {itemsExpanded ? "Tutup daftar produk" : `Lihat ${o.items.length - 1} produk lainnya`}
                    <ChevronDown className={`w-3.5 h-3.5 transition-transform ${itemsExpanded ? "rotate-180" : ""}`} />
                  </button>
                )}
                {os === "siap_dikirim" && PAID_PS.includes(o.payment_status) && !o.shipping_info?.awb && (
                  <div className="mt-2.5 flex gap-2 flex-wrap items-center" data-testid={`resi-inline-${o.code}`}>
                    <div className="h-9 px-3 rounded-lg border border-slate-200 bg-slate-50 text-xs flex items-center font-semibold text-slate-700">
                      {o.shipping_info?.service_name || o.shipping_info?.courier || o.courier || "JNE/SPX"}
                    </div>
                    <input data-testid={`resi-input-${o.code}`} value={resiMap[o.id] || ""} onChange={(e) => setResiMap((m) => ({ ...m, [o.id]: e.target.value }))}
                      placeholder="Input nomor resi" className="h-9 px-3 rounded-lg border border-slate-200 text-xs font-mono w-44" />
                    <button data-testid={`resi-save-${o.code}`} onClick={() => saveResiInline(o)} className="h-9 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition-colors">Simpan Resi</button>
                  </div>
                )}
              </div>
              <div className="min-w-[130px]">
                <div className="text-[10px] uppercase tracking-wide text-slate-400">Total • {PAY_SHORT[o.payment_method] || o.payment_method}</div>
                <div className="font-bold text-kiyana-maroon">{formatIDR(o.total)}</div>
              </div>
              <div className="min-w-[150px]">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Status</div>
                <div className="flex flex-wrap gap-1">
                  <span className={`text-[10px] px-2 py-0.5 rounded-full ${o.payment_status === "sudah_dibayar" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>{o.payment_status?.replace(/_/g, " ")}</span>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">{os.replace(/_/g, " ") || "-"}</span>
                  {o.cancel_request?.status === "menunggu_persetujuan" && <span data-testid={`cancel-badge-${o.code}`} className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Pembatalan diajukan</span>}
                  {o.payment_status === "cod_menunggu_verifikasi" && <span data-testid={`cod-badge-${o.code}`} className="text-[10px] px-2 py-0.5 rounded-full bg-purple-100 text-purple-700">COD perlu verifikasi</span>}
                </div>
              </div>
              <div className="min-w-[150px]">
                <div className="text-[10px] uppercase tracking-wide text-slate-400 mb-1">Jasa Kirim</div>
                {o.shipping_info ? (
                  <div className="text-xs text-slate-600">
                    {o.shipping_info.service_name || o.shipping_info.courier}
                    {o.shipping_info.awb && <div className="font-mono text-[11px] text-slate-800 mt-0.5" data-testid={`awb-${o.code}`}>{o.shipping_info.courier} {o.shipping_info.awb}</div>}
                  </div>
                ) : <div className="text-xs text-slate-400">-</div>}
              </div>
              <div className="flex flex-col gap-1.5 items-end ml-auto">
                {o.payment_status === "menunggu_verifikasi_manual" && (
                  <>
                    <button data-testid={`pay-approve-${o.code}`} onClick={() => payAct(o.id, "terima")}
                      className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold transition-colors">Terima Pembayaran</button>
                    <button data-testid={`pay-reject-${o.code}`} onClick={() => setPayRejectId(payRejectId === o.id ? null : o.id)}
                      className="h-9 px-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">Tolak Pembayaran</button>
                    {payRejectId === o.id && (
                      <div className="flex gap-1.5">
                        <input data-testid="pay-reject-note" value={payReason} onChange={(e) => setPayReason(e.target.value)} placeholder="Alasan penolakan (wajib)" className="h-9 px-2.5 rounded-lg border border-slate-200 text-xs w-44" />
                        <button data-testid="pay-reject-confirm" onClick={() => payAct(o.id, "tolak")} className="h-9 px-3 rounded-lg bg-red-600 text-white text-xs font-semibold">Tolak</button>
                      </div>
                    )}
                  </>
                )}
                {["menunggu_pembayaran_manual", "menunggu_pembayaran_xendit"].includes(o.payment_status) && (
                  <span data-testid={`unpaid-badge-${o.code}`} className="text-[10px] px-2 py-1 rounded-full bg-slate-100 text-slate-500 font-semibold">Belum Menerima Pembayaran</span>
                )}
                {needProcess && (
                  <button data-testid={`proses-btn-${o.code}`} onClick={() => setStatus(o.id, "siap_dikirim")}
                    className="h-9 px-4 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-xs font-semibold transition-colors">Proses Pesanan</button>
                )}
                {(needProcess || inPrep || os === "dikirim") && (
                  <button data-testid={`print-label-${o.code}`} onClick={() => window.open(`/admin/print/label/${o.id}`, "_blank")}
                    className="h-9 px-4 rounded-lg border border-violet-300 text-violet-700 text-xs font-semibold flex items-center gap-1.5 hover:bg-violet-50 transition-colors"><Printer className="w-3.5 h-3.5" /> Cetak Label A6</button>
                )}
                {os === "dikirim" && o.shipping_info?.awb && (
                  <button data-testid={`track-btn-${o.code}`} onClick={() => toggleTrack(o)}
                    className="h-9 px-4 rounded-lg border border-blue-300 text-blue-700 text-xs font-semibold flex items-center gap-1.5 hover:bg-blue-50 transition-colors">
                    <Truck className="w-3.5 h-3.5" /> {trackOpen === o.id ? "Tutup Lacak" : "Lacak Pesanan"}
                  </button>
                )}
                <button data-testid={`admin-order-${o.code}`} onClick={() => setOpen(open === o.id ? null : o.id)}
                  className="text-xs text-violet-700 font-semibold flex items-center gap-1">
                  {open === o.id ? "Tutup Rincian" : "Lihat Rincian"} <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open === o.id ? "rotate-180" : ""}`} />
                </button>
              </div>
            </div>
            {trackOpen === o.id && (
              <div className="px-4 pb-4 pt-3 border-t border-slate-100" data-testid={`track-panel-${o.code}`}>
                {trackLoading === o.id ? (
                  <div className="text-xs text-slate-400">Melacak resi {o.shipping_info?.awb}...</div>
                ) : trackData[o.id]?.error ? (
                  <div className="text-xs text-red-500">Tracking gagal / belum tersedia — status pesanan tetap Dikirim.</div>
                ) : trackData[o.id] && (
                  <>
                    <div className="flex items-center gap-2 mb-2 text-xs flex-wrap">
                      <span className="font-mono font-semibold text-slate-700">{trackData[o.id].courier?.toUpperCase()} {trackData[o.id].awb}</span>
                      <span className={`px-2 py-0.5 rounded-full font-semibold ${trackData[o.id].delivered ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"}`}>
                        {trackData[o.id].delivered ? "Delivered — otomatis Selesai via tracking" : "Dalam perjalanan"}
                      </span>
                      {trackData[o.id].mock && <span className="text-slate-400">(mode simulasi)</span>}
                    </div>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto">
                      {(trackData[o.id].events || []).map((ev, i) => (
                        <div key={i} className="flex gap-2 text-xs">
                          <span className="text-slate-400 whitespace-nowrap">{ev.date}</span>
                          <span className="text-slate-700">{ev.status}</span>
                        </div>
                      ))}
                      {!(trackData[o.id].events || []).length && <div className="text-xs text-slate-400">Belum ada riwayat perjalanan.</div>}
                    </div>
                  </>
                )}
              </div>
            )}
            {open === o.id && (
              <div className="px-4 pb-4 pt-3 border-t border-slate-100 bg-slate-50 grid md:grid-cols-2 gap-4 rounded-b-xl">
                <div>
                  {needProcess && <div className="text-[11px] text-violet-700 bg-violet-50 border border-violet-200 rounded-lg px-2.5 py-1.5 mb-2">Pembayaran sudah terkonfirmasi — kamu bisa <b>Cetak Label A6</b> sekarang (status tidak berubah), lalu klik <b>Proses Pesanan</b> setelah barang selesai dipacking → status menjadi <b>Siap Kirim</b>.</div>}
                  {inPrep && !o.shipping_info?.awb && <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5 mb-2">Pesanan <b>Siap Kirim</b> — pilih jasa ekspedisi dan input nomor resi; status otomatis menjadi <b>Dikirim</b> setelah resi tersimpan.</div>}
                </div>
                <div>
                  <div className="text-xs font-semibold mb-2">Pengiriman & Resi <span className="font-normal text-slate-400">(status otomatis jadi Terkirim setelah resi tersimpan)</span></div>
                  {o.shipping_info && (
                    <div className="text-xs text-slate-500 mb-2">
                      {o.shipping_info.service_name || o.courier} • {formatIDR(o.shipping_info.cost)} • Estimasi {o.shipping_info.etd}
                      {o.shipping_info.awb_input_by && <div className="text-[10px] text-slate-400 mt-0.5">Resi diinput oleh {o.shipping_info.awb_input_by}{o.shipping_info.awb_input_at ? ` • ${formatDateID(o.shipping_info.awb_input_at)}` : ""}</div>}
                    </div>
                  )}
                  {(["diproses", "dikemas", "siap_dikirim"].includes(os) || (os === "dikirim" && !o.shipping_info?.awb)) && PAID_PS.includes(o.payment_status) ? (
                    <div className="flex gap-2">
                      <div className="h-10 px-3 rounded-lg border border-slate-200 bg-white text-xs flex items-center font-semibold text-slate-700" data-testid="ship-courier">
                        {o.shipping_info?.courier || "JNE/SPX"} <span className="ml-1 font-normal text-slate-400">(dipilih pembeli saat checkout)</span>
                      </div>
                      <input data-testid="ship-awb" value={ship.awb} onChange={(e) => setShip({ awb: e.target.value })} placeholder="Nomor resi (wajib untuk Dikirim)" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-xs font-mono" />
                      <button data-testid="ship-save" onClick={() => saveShipment(o.id)} className="h-10 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold">Simpan Resi</button>
                    </div>
                  ) : os === "dikirim" && o.shipping_info?.awb ? (
                    <div className="text-[11px] text-slate-500 border border-slate-200 rounded-lg px-3 py-2.5 bg-white" data-testid="ship-done-note">
                      Resi <b className="font-mono">{o.shipping_info.courier} {o.shipping_info.awb}</b> sudah tersimpan. Gunakan tombol <b>Lacak Pesanan</b> di kartu untuk melacak — status otomatis menjadi Selesai saat tracking ekspedisi menyatakan Delivered.
                    </div>
                  ) : (
                    <div className="text-[11px] text-slate-400 border border-dashed border-slate-200 rounded-lg px-3 py-2.5" data-testid="ship-locked-note">Input resi hanya tersedia saat pesanan berstatus <b>Siap Kirim</b>.</div>
                  )}
                </div>
                {o.payment_method === "cod" && (
                  <div className="md:col-span-2 border-t border-slate-200 pt-3">
                    <div className="text-xs font-semibold mb-1 text-kiyana-maroon">COD — Bayar di Tempat</div>
                    {o.cod_form && (
                      <div className="text-xs text-slate-600 space-y-1 mb-2">
                        <div>Nama KTP: <b>{o.cod_form.ktp_name}</b> • No: <span className="font-mono">{o.cod_form.ktp_number}</span> • HP: {o.cod_form.ktp_phone}</div>
                        <a data-testid={`cod-ktp-${o.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${o.cod_form.ktp_image_url}`} target="_blank" rel="noreferrer" className="text-kiyana-maroon underline inline-block">Lihat foto KTP/Paspor</a>
                      </div>
                    )}
                    <div className="text-xs text-slate-500 mb-2">Fee COD: {formatIDR(o.cod_fee || 0)} ({o.cod_fee_percent || 0}% dari total belanja)</div>
                    {o.payment_status === "cod_menunggu_verifikasi" && (
                      <div className="flex flex-wrap gap-2">
                        <button data-testid={`cod-approve-${o.id}`} onClick={() => codAct(o.id, "setuju")} className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Setujui COD → Menunggu Diproses</button>
                        <button data-testid={`cod-reject-${o.id}`} onClick={() => setCodRejectId(codRejectId === o.id ? null : o.id)} className="h-9 px-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">Tolak</button>
                      </div>
                    )}
                    {o.payment_status === "cod_belum_terbayar" && (
                      <div className="flex flex-wrap gap-2">
                        <button data-testid={`cod-paid-${o.id}`} onClick={() => codAct(o.id, "lunas")} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-xs font-semibold">Tandai Lunas — COD Sudah Dibayar</button>
                        <button data-testid={`cod-refused-${o.id}`} onClick={() => { if (window.confirm("Tandai paket COD ditolak pembeli? Akun pembeli akan diblokir dari metode COD.")) codAct(o.id, "ditolak_pembeli"); }} className="h-9 px-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">Pembeli Menolak Paket</button>
                      </div>
                    )}
                    {o.cod_rejected_note && <div className="text-xs text-red-500 mt-1.5">COD ditolak: {o.cod_rejected_note}</div>}
                    {codRejectId === o.id && (
                      <div className="flex gap-2 mt-2">
                        <input data-testid="cod-reject-note" value={codNote} onChange={(e) => setCodNote(e.target.value)} placeholder="Alasan penolakan (wajib)" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-xs" />
                        <button data-testid="cod-reject-confirm" onClick={() => codAct(o.id, "tolak")} className="h-10 px-4 rounded-lg bg-red-600 text-white text-xs font-semibold">Tolak COD</button>
                      </div>
                    )}
                  </div>
                )}
                {o.cancel_request?.status === "menunggu_persetujuan" && (
                  <div className="md:col-span-2 border-t border-slate-200 pt-3">
                    <div className="text-xs font-semibold mb-1 text-orange-600">Pengajuan Pembatalan Pelanggan</div>
                    <div className="text-xs text-slate-600">Alasan: {o.cancel_request.reason}</div>
                    <div className="text-[11px] text-slate-400 mt-0.5 mb-2">Jika disetujui, pesanan dibatalkan dan dana dikembalikan manual (transfer balik).</div>
                    <div className="flex flex-wrap gap-2">
                      <button data-testid={`cancel-approve-${o.id}`} onClick={() => cancelAct(o.id, "setuju")} className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Setujui Pembatalan</button>
                      <button data-testid={`cancel-reject-${o.id}`} onClick={() => setCancelRejectId(cancelRejectId === o.id ? null : o.id)} className="h-9 px-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">Tolak</button>
                    </div>
                    {cancelRejectId === o.id && (
                      <div className="flex gap-2 mt-2">
                        <input data-testid="cancel-reject-note" value={cancelNote} onChange={(e) => setCancelNote(e.target.value)} placeholder="Alasan penolakan (wajib)" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-xs" />
                        <button data-testid="cancel-reject-confirm" onClick={() => cancelAct(o.id, "tolak")} className="h-10 px-4 rounded-lg bg-red-600 text-white text-xs font-semibold">Tolak Pembatalan</button>
                      </div>
                    )}
                  </div>
                )}
                {o.cancel_request && o.cancel_request.status !== "menunggu_persetujuan" && (
                  <div data-testid={`cancel-history-${o.id}`} className="md:col-span-2 border-t border-slate-200 pt-2 text-[11px] text-slate-500">
                    Riwayat pembatalan: pengajuan <b>{o.cancel_request.status}</b>
                    {o.cancel_request.rejected_note ? ` — ${o.cancel_request.rejected_note}` : ""}
                    {o.cancel_request.reviewed_by ? ` (oleh ${o.cancel_request.reviewed_by})` : ""}
                  </div>
                )}
                {(o.order_status || "").match(/^(retur|refund)/) && (
                  <div className="md:col-span-2 border-t border-slate-200 pt-3">
                    <div className="text-xs font-semibold mb-2">Retur / Refund — <span className="text-kiyana-maroon capitalize">{o.order_status.replace(/_/g, " ")}</span></div>
                    <div className="text-xs text-slate-600 space-y-1">
                      <div>Alasan: {o.retur_reason || "-"}</div>
                      {o.retur_proof && <a data-testid={`retur-proof-${o.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${o.retur_proof}`} target="_blank" rel="noreferrer" className="text-kiyana-maroon underline inline-block">Lihat bukti foto/video</a>}
                      {o.retur_awb && <div>Resi pengembalian: <b className="font-mono">{o.retur_courier} {o.retur_awb}</b></div>}
                    </div>
                    <div className="flex flex-wrap gap-2 mt-2.5">
                      {o.order_status === "retur_diajukan" && (
                        <>
                          <button data-testid={`retur-approve-${o.id}`} onClick={() => returAct(o.id, "approve")} className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Setujui Retur</button>
                          <button data-testid={`retur-reject-${o.id}`} onClick={() => setReturRejectId(returRejectId === o.id ? null : o.id)} className="h-9 px-4 rounded-lg bg-red-50 border border-red-200 text-red-600 text-xs font-semibold">Tolak</button>
                        </>
                      )}
                      {o.order_status === "retur_dikirim" && (
                        <button data-testid={`retur-received-${o.id}`} onClick={() => returAct(o.id, "received")} className="h-9 px-4 rounded-lg bg-blue-600 text-white text-xs font-semibold">Paket Diterima → Proses Refund</button>
                      )}
                      {o.order_status === "refund_diproses" && (
                        <button data-testid={`retur-refunded-${o.id}`} onClick={() => returAct(o.id, "refunded")} className="h-9 px-4 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold">Tandai Refund Selesai</button>
                      )}
                    </div>
                    {returRejectId === o.id && (
                      <div className="flex gap-2 mt-2">
                        <input data-testid="retur-reject-reason" value={returReason} onChange={(e) => setReturReason(e.target.value)} placeholder="Alasan penolakan (wajib)" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-xs" />
                        <button data-testid="retur-reject-confirm" onClick={() => returAct(o.id, "reject")} className="h-10 px-4 rounded-lg bg-red-600 text-white text-xs font-semibold">Tolak Retur</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

export function AdminReturns() {
  return <AdminOrders preset="returns" />;
}

const PROD_TABS = [
  { id: "semua", label: "Semua" },
  { id: "aktif", label: "Aktif" },
  { id: "draft", label: "Draft" },
  { id: "nonaktif", label: "Nonaktif" },
];
const PROD_STATUS_LABEL = { aktif: "Aktif", sedang_ditinjau: "Sedang Ditinjau", draft: "Draft", nonaktif: "Nonaktif" };
const PROD_STATUS_CLS = {
  aktif: "bg-emerald-50 text-emerald-600",
  sedang_ditinjau: "bg-amber-50 text-amber-600",
  draft: "bg-slate-100 text-slate-500",
  nonaktif: "bg-red-50 text-red-500",
};

function prodStock(p) {
  return Array.isArray(p.variants) && p.variants.length
    ? p.variants.reduce((s, v) => s + (v.stock || 0), 0)
    : (p.stock ?? 0);
}

const PROD_PAGE = 10;

export function AdminProducts() {
  const nav = useNavigate();
  const { hasPerm } = useAuth();
  const canManage = hasPerm("products.manage");
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [supMap, setSupMap] = useState({});
  const [tab, setTab] = useState("semua");
  const [q, setQ] = useState("");
  const [qApplied, setQApplied] = useState("");
  const [cat, setCat] = useState("all");
  const [stockF, setStockF] = useState("all");
  const [sort, setSort] = useState("terbaru");
  const [expanded, setExpanded] = useState(new Set());
  const [menuFor, setMenuFor] = useState(null);
  const [page, setPage] = useState(1);
  const [prodPer, setProdPer] = useState(PROD_PAGE);

  const load = () => api.get("/admin/products").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
    api.get("/admin/suppliers").then((r) => setSupMap(Object.fromEntries(r.data.map((s) => [s.id, s.name])))).catch(() => {});
  }, []);

  const counts = useMemo(() => {
    const c = { semua: items.length, aktif: 0, draft: 0, nonaktif: 0 };
    items.forEach((p) => { const s = p.status || "aktif"; if (c[s] !== undefined) c[s] += 1; });
    return c;
  }, [items]);

  const filtered = useMemo(() => {
    let arr = items.filter((p) => tab === "semua" || (p.status || "aktif") === tab);
    if (qApplied) {
      const s = qApplied.toLowerCase();
      arr = arr.filter((p) => p.name.toLowerCase().includes(s) || (p.sku || "").toLowerCase().includes(s) || p.id.toLowerCase().includes(s)
        || (p.variants || []).some((v) => (v.sku || "").toLowerCase().includes(s) || (v.barcode || "").toLowerCase().includes(s)));
    }
    if (cat !== "all") arr = arr.filter((p) => p.category === cat);
    if (stockF !== "all") arr = arr.filter((p) => {
      const st = prodStock(p);
      const min = p.min_stock ?? 10;
      return stockF === "habis" ? st === 0 : stockF === "menipis" ? st > 0 && st <= min : st > min;
    });
    const sorted = [...arr];
    if (sort === "terlama") sorted.sort((a, b) => (a.created_at || "").localeCompare(b.created_at || ""));
    else if (sort === "nama") sorted.sort((a, b) => a.name.localeCompare(b.name));
    else if (sort === "stok_desc") sorted.sort((a, b) => prodStock(b) - prodStock(a));
    else if (sort === "stok_asc") sorted.sort((a, b) => prodStock(a) - prodStock(b));
    else if (sort === "harga_desc") sorted.sort((a, b) => b.price - a.price);
    else if (sort === "harga_asc") sorted.sort((a, b) => a.price - b.price);
    else sorted.sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    return sorted;
  }, [items, tab, qApplied, cat, stockF, sort]);

  useEffect(() => { setPage(1); }, [tab, qApplied, cat, stockF, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / prodPer));
  const pageItems = filtered.slice((page - 1) * prodPer, page * prodPer);

  const toggle = (id) => setExpanded((s) => { const n = new Set(s); if (n.has(id)) { n.delete(id); } else { n.add(id); } return n; });
  const applySearch = () => setQApplied(q.trim());

  const importRef = useRef(null);
  const importFileRef = useRef(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const postImportFile = (path, blob = false) => {
    const fd = new FormData();
    fd.append("file", importFileRef.current);
    return api.post(path, fd, blob ? { responseType: "blob" } : {});
  };

  const doExport = async () => {
    try {
      const r = await api.get("/admin/products/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `produk-kiyana-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("File export produk terunduh");
    } catch { toast.error("Gagal mengunduh export"); }
  };

  const doImport = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    importFileRef.current = f;
    setImporting(true);
    try {
      const { data } = await postImportFile("/admin/products/import/scan");
      setImportResult({ phase: "scan", ...data });
    } catch (err) { toast.error(err?.response?.data?.detail || "Gagal memindai file"); }
    finally { setImporting(false); }
  };

  const downloadCorrection = async () => {
    try {
      const r = await postImportFile("/admin/products/import/correction", true);
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `koreksi-import-${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Excel koreksi terunduh — kolom bermasalah ditandai merah");
    } catch { toast.error("Gagal mengunduh excel koreksi"); }
  };

  const proceedImport = async () => {
    setImporting(true);
    try {
      const { data } = await postImportFile("/admin/products/import");
      setImportResult({ phase: "done", ...data });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import gagal"); }
    finally { setImporting(false); }
  };

  const setStatus = async (p, status) => {
    try {
      await api.patch(`/admin/products/${p.id}/status`, { status });
      toast.success(`${p.name} kini ${PROD_STATUS_LABEL[status]}`);
      setMenuFor(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal mengubah status"); }
  };

  return (
    <div data-testid="admin-products-page" className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Produk Saya</h1>
          <p className="text-xs text-slate-500">Kelola katalog produk toko — promo diatur di menu Voucher &amp; Promo</p>
        </div>
        <div className="flex items-center gap-2">
          {canManage && (
            <button data-testid="prod-ai-btn" onClick={() => nav("/admin/products/ai")}
              className="h-10 px-4 rounded-xl bg-slate-900 text-white text-xs font-semibold flex items-center gap-2 hover:bg-slate-800 transition-colors">
              <Sparkles className="w-4 h-4 text-[#E8A2B8]" /> Tambah Produk dengan AI
            </button>
          )}
          <button data-testid="prod-export-btn" onClick={doExport}
            className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-2 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
            <Download className="w-4 h-4" /> Export
          </button>
          {canManage && (
            <button data-testid="prod-import-btn" onClick={() => importRef.current?.click()} disabled={importing}
              className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-semibold flex items-center gap-2 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors disabled:opacity-50">
              <Upload className="w-4 h-4" /> {importing ? "Mengimpor..." : "Import"}
            </button>
          )}
          <input ref={importRef} data-testid="prod-import-file" type="file" accept=".xlsx" className="hidden" onChange={doImport} />
          <button data-testid="prod-barcode-btn" onClick={() => nav("/admin/products/barcode")}
            className="h-10 px-4 rounded-xl border border-[#C4738A]/40 text-[#C4738A] text-xs font-semibold flex items-center gap-2 hover:bg-[#C4738A]/5 transition-colors">
            <Printer className="w-4 h-4" /> Cetak Barcode
          </button>
          {canManage && (
            <button data-testid="prod-add-btn" onClick={() => nav("/admin/products/new")}
              className="h-10 px-4 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-2 transition-colors">
              <Plus className="w-4 h-4" /> Tambah Produk
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 border-b border-slate-200 overflow-x-auto">
        {PROD_TABS.map((t) => (
          <button key={t.id} data-testid={`prod-tab-${t.id}`} onClick={() => setTab(t.id)}
            className={`px-4 py-2.5 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${tab === t.id ? "border-[#C4738A] text-[#C4738A]" : "border-transparent text-slate-500 hover:text-slate-800"}`}>
            {t.label}{t.id !== "semua" && counts[t.id] > 0 ? ` (${counts[t.id]})` : ""}
          </button>
        ))}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input data-testid="prod-search-input" value={q} onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && applySearch()}
            placeholder="Nama produk, ID, SKU, atau barcode"
            className="h-10 w-full pl-9 pr-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-700 focus:outline-none focus:border-[#C4738A]" />
        </div>
        <select data-testid="prod-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)}
          className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#C4738A]">
          <option value="all">Kategori</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select data-testid="prod-stock-filter" value={stockF} onChange={(e) => setStockF(e.target.value)}
          className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#C4738A]">
          <option value="all">Status Stok</option>
          <option value="tersedia">Tersedia</option>
          <option value="menipis">Menipis</option>
          <option value="habis">Habis (0)</option>
        </select>
        <button data-testid="prod-search-btn" onClick={applySearch}
          className="h-10 px-6 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors">Cari</button>
        <select data-testid="prod-sort" value={sort} onChange={(e) => setSort(e.target.value)}
          className="h-10 px-3 rounded-xl bg-white border border-slate-200 text-xs text-slate-600 focus:outline-none focus:border-[#C4738A] ml-auto">
          <option value="terbaru">Urutkan: Terbaru</option>
          <option value="terlama">Urutkan: Terlama</option>
          <option value="nama">Urutkan: Nama A–Z</option>
          <option value="stok_desc">Urutkan: Stok Terbanyak</option>
          <option value="stok_asc">Urutkan: Stok Tersedikit</option>
          <option value="harga_desc">Urutkan: Harga Tertinggi</option>
          <option value="harga_asc">Urutkan: Harga Terendah</option>
        </select>
      </div>

      <p className="text-[11px] text-slate-400">Klik variasi untuk melihat SKU, barcode, stok, harga jual dan harga modal</p>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-slate-100 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] text-slate-500 border-b border-slate-100 bg-slate-50/60">
              <th className="px-5 py-3.5 font-medium">Produk</th>
              <th className="px-4 py-3.5 font-medium">Status</th>
              <th className="px-4 py-3.5 font-medium">Stok</th>
              <th className="px-4 py-3.5 font-medium">Harga Jual</th>
              <th className="px-5 py-3.5 font-medium text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map((p) => {
              const st = prodStock(p);
              const min = p.min_stock ?? 10;
              const status = p.status || "aktif";
              const vars = Array.isArray(p.variants) ? p.variants : [];
              const open = expanded.has(p.id);
              return (
                <FragmentRow key={p.id}>
                  <tr data-testid={`prod-row-${p.id}`} className={`border-b border-slate-50 transition-colors ${open ? "bg-rose-50/30" : "hover:bg-slate-50/50"}`}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        {p.image
                          ? <img src={p.image} alt={p.name} className="w-12 h-12 rounded-xl object-cover border border-slate-100 flex-shrink-0" />
                          : <div className="w-12 h-12 rounded-xl bg-slate-100 flex-shrink-0" />}
                        <div className="min-w-0">
                          <div className="text-xs font-semibold text-slate-800 truncate max-w-[280px]">{p.name}</div>
                          <div className="text-[10px] text-slate-400">SKU: {p.sku}</div>
                          {p.supplier_id && supMap[p.supplier_id] && (
                            <span data-testid={`prod-supplier-${p.id}`} className="inline-flex items-center gap-1 mt-0.5 text-[9px] font-semibold bg-violet-50 text-violet-600 rounded px-1.5 py-0.5">Supplier: {supMap[p.supplier_id]}</span>
                          )}
                          {vars.length > 0 && (
                            <button data-testid={`prod-variant-toggle-${p.id}`} onClick={() => toggle(p.id)}
                              className="mt-1 flex items-center gap-1 text-[11px] font-medium text-slate-500 hover:text-[#C4738A] transition-colors">
                              <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? "rotate-180" : ""}`} /> {open ? "Sembunyikan Variasi" : `Lihat Variasi (${vars.length})`}
                            </button>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span data-testid={`prod-status-${p.id}`} className={`inline-flex items-center gap-1.5 text-[11px] font-semibold px-2.5 py-1 rounded-full ${PROD_STATUS_CLS[status] || PROD_STATUS_CLS.draft}`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${status === "aktif" ? "bg-emerald-500" : status === "nonaktif" ? "bg-red-400" : "bg-slate-400"}`} />
                        {PROD_STATUS_LABEL[status] || status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-xs font-semibold ${st === 0 ? "text-red-500" : st <= min ? "text-amber-500" : "text-slate-800"}`}>{st.toLocaleString("id-ID")}</span>
                      {st > 0 && st <= min && <div className="text-[9px] text-amber-500">Menipis</div>}
                      {st === 0 && <div className="text-[9px] text-red-400">Habis</div>}
                    </td>
                    <td className="px-4 py-3 text-xs font-bold text-slate-900 whitespace-nowrap">{formatIDR(p.price)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center justify-end gap-2">
                        {canManage && (
                          <button data-testid={`prod-edit-${p.id}`} onClick={() => nav(`/admin/products/${p.id}/edit`)}
                            className="h-8 px-4 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors flex items-center gap-1.5">
                            <Pencil className="w-3 h-3" /> Edit
                          </button>
                        )}
                        <div className="relative">
                          <button data-testid={`prod-more-${p.id}`} onClick={() => setMenuFor(menuFor === p.id ? null : p.id)}
                            className="w-8 h-8 rounded-lg border border-slate-200 flex items-center justify-center text-slate-400 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">
                            <MoreVertical className="w-3.5 h-3.5" />
                          </button>
                          {menuFor === p.id && (
                            <>
                              <div className="fixed inset-0 z-40" onClick={() => setMenuFor(null)} />
                              <div data-testid={`prod-menu-${p.id}`} className="absolute right-0 top-9 z-50 bg-white border border-slate-200 rounded-xl shadow-lg w-44 py-1.5">
                                <button onClick={() => nav(`/admin/products/barcode?product=${p.id}`)} className="w-full text-left px-4 py-2 text-xs text-slate-700 hover:bg-slate-50 flex items-center gap-2">
                                  <Printer className="w-3.5 h-3.5 text-slate-400" /> Cetak Barcode
                                </button>
                                {canManage && status !== "aktif" && (
                                  <button data-testid={`prod-activate-${p.id}`} onClick={() => setStatus(p, "aktif")} className="w-full text-left px-4 py-2 text-xs text-emerald-600 hover:bg-emerald-50 flex items-center gap-2">
                                    <Check className="w-3.5 h-3.5" /> Aktifkan
                                  </button>
                                )}
                                {canManage && status === "aktif" && (
                                  <button data-testid={`prod-deactivate-${p.id}`} onClick={() => setStatus(p, "nonaktif")} className="w-full text-left px-4 py-2 text-xs text-red-500 hover:bg-red-50 flex items-center gap-2">
                                    <Power className="w-3.5 h-3.5" /> Nonaktifkan
                                  </button>
                                )}
                                {canManage && status !== "draft" && (
                                  <button data-testid={`prod-draft-${p.id}`} onClick={() => setStatus(p, "draft")} className="w-full text-left px-4 py-2 text-xs text-slate-600 hover:bg-slate-50 flex items-center gap-2">
                                    <FileText className="w-3.5 h-3.5 text-slate-400" /> Jadikan Draft
                                  </button>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                  {open && vars.length > 0 && (
                    <tr className="border-b border-slate-100">
                      <td colSpan={5} className="bg-slate-50/50 px-6 py-3">
                        <div data-testid={`prod-variants-${p.id}`} className="bg-white rounded-xl border border-slate-100 overflow-hidden overflow-x-auto">
                          <table className="w-full min-w-[760px]">
                            <thead>
                              <tr className="text-[10px] uppercase tracking-wider text-slate-400 border-b border-slate-100">
                                <th className="text-left font-medium px-4 py-2">Variasi</th>
                                <th className="text-left font-medium px-3 py-2">SKU</th>
                                <th className="text-left font-medium px-3 py-2">Barcode</th>
                                <th className="text-center font-medium px-3 py-2">Stok</th>
                                <th className="text-right font-medium px-3 py-2">Harga Jual</th>
                                {canManage && <th className="text-right font-medium px-3 py-2">Harga Modal</th>}
                                <th className="text-right font-medium px-4 py-2">Aksi</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-50">
                              {vars.map((v, vi) => (
                                <tr key={vi} data-testid={`prod-variant-${p.id}-${vi}`} className={`hover:bg-slate-50/40 transition-colors ${v.active === false ? "opacity-50" : ""}`}>
                                  <td className="px-4 py-2.5">
                                    <div className="flex items-center gap-2.5">
                                      {(v.image || p.image)
                                        ? <img src={v.image || p.image} alt="" className="w-9 h-9 rounded-lg object-cover border border-slate-100 flex-shrink-0" />
                                        : <div className="w-9 h-9 rounded-lg bg-slate-100 flex-shrink-0" />}
                                      <div>
                                        <span className="text-xs font-medium text-slate-700">{v.color} / {v.size}</span>
                                        {v.active === false && <span className="ml-1.5 text-[9px] font-bold text-red-400">NONAKTIF</span>}
                                      </div>
                                    </div>
                                  </td>
                                  <td className="px-3 py-2.5 text-[11px] font-mono text-slate-500">{v.sku || "—"}</td>
                                  <td className="px-3 py-2.5 text-[11px] font-mono text-slate-500">{v.barcode || "—"}</td>
                                  <td className="px-3 py-2.5 text-center">
                                    <span className={`text-[11px] font-semibold rounded-md px-2 py-0.5 ${(v.stock || 0) === 0 ? "bg-red-50 text-red-500" : (v.stock || 0) <= min ? "bg-amber-50 text-amber-600" : "bg-slate-100 text-slate-700"}`}>{v.stock ?? 0}</span>
                                  </td>
                                  <td className="px-3 py-2.5 text-right text-xs font-semibold text-slate-800">{formatIDR(v.price || p.price)}</td>
                                  {canManage && <td className="px-3 py-2.5 text-right text-xs text-slate-500">{v.cost_price ? formatIDR(v.cost_price) : "—"}</td>}
                                  <td className="px-4 py-2.5 text-right">
                                    {canManage && <button onClick={() => nav(`/admin/products/${p.id}/edit`)} className="text-[11px] font-semibold text-[#C4738A] hover:underline">Edit</button>}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </td>
                    </tr>
                  )}
                </FragmentRow>
              );
            })}
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="px-5 py-14 text-center text-xs text-slate-400" data-testid="prod-empty">Tidak ada produk yang cocok dengan filter ini.</td></tr>
            )}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-slate-400">per halaman</span>
            <select data-testid="prod-pager-per" value={prodPer} onChange={(e) => { setProdPer(parseInt(e.target.value)); setPage(1); }}
              className="h-8 px-2 rounded-lg border border-slate-200 text-xs bg-white focus:outline-none focus:border-[#C4738A]">
              {[10, 50, 100, 300].map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </div>
          <span className="text-[11px] text-slate-500" data-testid="prod-pager-info">Menampilkan {filtered.length === 0 ? 0 : (page - 1) * prodPer + 1}–{Math.min(filtered.length, page * prodPer)} dari {filtered.length} produk</span>
          <div className="flex items-center gap-1">
            <button data-testid="prod-pager-prev" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1}
              className="h-8 px-3 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 disabled:opacity-40 hover:border-[#C4738A] transition-colors">‹ Prev</button>
            {Array.from({ length: pages }, (_, i) => i + 1).slice(0, 7).map((n) => (
              <button key={n} data-testid={`prod-pager-${n}`} onClick={() => setPage(n)}
                className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${n === page ? "bg-[#C4738A] text-white" : "border border-slate-200 text-slate-600 hover:border-[#C4738A]"}`}>{n}</button>
            ))}
            <button data-testid="prod-pager-next" onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages}
              className="h-8 px-3 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 disabled:opacity-40 hover:border-[#C4738A] transition-colors">Next ›</button>
          </div>
        </div>
      </div>
      {importResult && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" onClick={() => setImportResult(null)}>
          <div data-testid="prod-import-result" className="bg-white rounded-2xl shadow-xl w-full max-w-lg p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display font-bold text-sm text-slate-900">
              {importResult.phase === "scan" ? "Hasil Pemindaian File" : "Hasil Import Produk"}
            </h3>
            {importResult.phase === "scan" ? (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-slate-50 py-2.5"><div className="text-lg font-bold text-slate-800">{importResult.total}</div><div className="text-[10px] text-slate-500">Total Produk</div></div>
                  <div className="rounded-xl bg-emerald-50 py-2.5"><div className="text-lg font-bold text-emerald-600">{importResult.valid}</div><div className="text-[10px] text-emerald-600">Data Valid</div></div>
                  <div className="rounded-xl bg-red-50 py-2.5"><div className="text-lg font-bold text-red-500">{importResult.invalid}</div><div className="text-[10px] text-red-500">Bermasalah</div></div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div data-testid="prod-import-errors" className="max-h-48 overflow-y-auto rounded-xl border border-slate-100 divide-y divide-slate-50">
                    {importResult.errors.map((er, i) => (
                      <div key={i} className="px-3 py-2 text-[11px] text-slate-600">
                        <span className="font-semibold">Baris {er.row}</span>{er.name ? ` • ${er.name}` : ""}
                        <span className="ml-1 text-[9px] font-bold uppercase tracking-wide bg-red-50 text-red-500 rounded px-1.5 py-0.5">{er.column}</span>
                        <div className="text-slate-400 mt-0.5">{er.message}</div>
                      </div>
                    ))}
                    {importResult.total_errors > importResult.errors.length && (
                      <div className="px-3 py-2 text-[10px] text-slate-400">+{importResult.total_errors - importResult.errors.length} error lainnya (lihat excel koreksi)</div>
                    )}
                  </div>
                )}
                <p className="text-[10px] text-slate-400">Belum ada data yang tersimpan. Unduh excel koreksi untuk memperbaiki baris bermasalah, atau lanjutkan import hanya untuk data valid.</p>
                <div className="flex items-center gap-2">
                  {importResult.invalid > 0 && (
                    <button data-testid="prod-import-correction" onClick={downloadCorrection}
                      className="h-10 px-4 rounded-xl border border-red-200 text-red-500 text-xs font-semibold flex items-center gap-1.5 hover:bg-red-50 transition-colors">
                      <Download className="w-3.5 h-3.5" /> Excel Koreksi
                    </button>
                  )}
                  <button data-testid="prod-import-proceed" onClick={proceedImport} disabled={importing || importResult.valid === 0}
                    className="h-10 px-5 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold transition-colors disabled:opacity-50 flex-1">
                    {importing ? "Mengimpor..." : `Lanjutkan Import (${importResult.valid} produk valid)`}
                  </button>
                  <button data-testid="prod-import-cancel" onClick={() => setImportResult(null)}
                    className="h-10 px-4 rounded-xl border border-slate-200 text-xs font-semibold text-slate-600 hover:border-slate-300 transition-colors">Batal</button>
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div className="rounded-xl bg-emerald-50 py-2.5"><div className="text-lg font-bold text-emerald-600">{importResult.created}</div><div className="text-[10px] text-emerald-600">Baru</div></div>
                  <div className="rounded-xl bg-sky-50 py-2.5"><div className="text-lg font-bold text-sky-600">{importResult.updated}</div><div className="text-[10px] text-sky-600">Diperbarui</div></div>
                  <div className="rounded-xl bg-amber-50 py-2.5"><div className="text-lg font-bold text-amber-600">{importResult.skipped}</div><div className="text-[10px] text-amber-600">Dilewati</div></div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="max-h-44 overflow-y-auto rounded-xl border border-slate-100 divide-y divide-slate-50">
                    {importResult.errors.map((er, i) => (
                      <div key={i} className="px-3 py-2 text-[11px] text-slate-600">
                        <span className="font-semibold">Baris {er.row}</span>{er.name ? ` • ${er.name}` : ""}
                        {er.column && <span className="ml-1 text-[9px] font-bold uppercase tracking-wide bg-red-50 text-red-500 rounded px-1.5 py-0.5">{er.column}</span>}
                        <div className="text-slate-400 mt-0.5">{er.message || er.error}</div>
                      </div>
                    ))}
                    {importResult.total_errors > importResult.errors.length && (
                      <div className="px-3 py-2 text-[10px] text-slate-400">+{importResult.total_errors - importResult.errors.length} error lainnya</div>
                    )}
                  </div>
                )}
                <button data-testid="prod-import-close" onClick={() => setImportResult(null)} className="w-full h-10 rounded-xl bg-[#C4738A] text-white text-xs font-semibold hover:bg-[#b3647b] transition-colors">Tutup</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function FragmentRow({ children }) {
  return <>{children}</>;
}

export function AdminEmployees() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [pinFor, setPinFor] = useState(null);
  const [pinVal, setPinVal] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/employees").then((r) => setItems(r.data)).catch(()=>{});
  useEffect(() => { load(); }, []);

  const savePin = async () => {
    if (!/^\d{6}$/.test(pinVal)) return setMsg("PIN harus tepat 6 digit angka");
    try {
      await api.put(`/admin/employees/${pinFor.id}/pos-pin`, { pin: pinVal });
      setMsg(`PIN POS ${pinFor.name} tersimpan (berlaku juga sebagai reset)`);
      setPinFor(null); setPinVal(""); load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan PIN"); }
  };
  const regen = async (u) => {
    try {
      const { data } = await api.post(`/admin/employees/${u.id}/card-code`);
      setMsg(`Kode ID Card baru untuk ${u.name}: ${data.card_code}`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal membuat kode kartu"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold">Karyawan</h1>
          <p className="text-sm text-slate-500">Atur PIN POS (6 digit) dan kode ID Card untuk login multi-kasir di terminal POS. PIN tersimpan ter-hash dan tidak pernah ditampilkan.</p>
        </div>
        <button data-testid="emp-add-btn" onClick={() => nav("/admin/employees/new")}
          className="h-10 px-4 rounded-xl bg-[#C4738A] hover:bg-[#b3647b] text-white text-xs font-semibold flex items-center gap-2 transition-colors">
          <Plus className="w-4 h-4" /> Tambah Karyawan
        </button>
      </div>
      {msg && <div data-testid="emp-msg" className="text-xs bg-violet-50 text-violet-700 border border-violet-200 rounded-lg px-3 py-2">{msg}</div>}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left p-3">ID</th><th className="text-left p-3">Nama</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Status</th><th className="text-left p-3">PIN POS</th><th className="text-left p-3">ID Card</th><th className="text-left p-3">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((u) => (
              <tr key={u.id} data-testid={`emp-row-${u.id}`}>
                <td className="p-3 text-[11px] font-mono text-slate-500">{u.employee_id || "—"}</td>
                <td className="p-3 font-semibold">{u.name}</td>
                <td className="p-3">{u.email}</td>
                <td className="p-3"><span className="text-xs bg-slate-100 rounded-full px-2 py-0.5 capitalize">{u.role}</span></td>
                <td className="p-3">{u.active ? <span className="text-xs text-emerald-600">Aktif</span> : <span className="text-xs text-red-600">Nonaktif</span>}</td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span data-testid={`emp-pin-status-${u.id}`} className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${u.has_pin ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-400"}`}>{u.has_pin ? "Sudah diatur" : "Belum"}</span>
                    <button data-testid={`emp-pin-set-${u.id}`} onClick={() => { setPinFor(u); setPinVal(""); setMsg(""); }} className="text-[11px] font-semibold text-violet-600 hover:text-violet-800 flex items-center gap-1"><Fingerprint className="w-3.5 h-3.5" /> {u.has_pin ? "Reset" : "Atur"}</button>
                  </div>
                </td>
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    <span data-testid={`emp-card-${u.id}`} className="text-xs font-mono text-slate-600">{u.card_code || "—"}</span>
                    <button data-testid={`emp-card-regen-${u.id}`} onClick={() => regen(u)} className="text-[11px] font-semibold text-violet-600 hover:text-violet-800 flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Acak Ulang</button>
                  </div>
                </td>
                <td className="p-3">
                  <button data-testid={`emp-edit-${u.id}`} onClick={() => nav(`/admin/employees/${u.id}/edit`)} className="h-8 px-3 rounded-lg border border-slate-200 text-[11px] font-semibold text-slate-600 hover:border-[#C4738A] hover:text-[#C4738A] transition-colors">Edit</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {pinFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setPinFor(null)}>
          <div className="bg-white rounded-2xl max-w-sm w-full p-5" onClick={(e) => e.stopPropagation()} data-testid="emp-pin-modal">
            <div className="flex items-center justify-between mb-1">
              <div className="font-display font-bold text-slate-900">Atur PIN POS — {pinFor.name}</div>
              <button data-testid="emp-pin-close" onClick={() => setPinFor(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-[11px] text-slate-500 mb-3">PIN 6 digit untuk login petugas di terminal POS. Berikan langsung ke karyawan — PIN tidak bisa dilihat kembali setelah disimpan.</p>
            <input data-testid="emp-pin-input" type="password" inputMode="numeric" maxLength={6} value={pinVal}
              onChange={(e) => setPinVal(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="••••••" className="w-full h-11 px-3 rounded-lg border border-slate-200 text-center tracking-[0.5em] font-mono text-sm focus:outline-none focus:border-violet-400" />
            <button data-testid="emp-pin-save" onClick={savePin} className="mt-3 w-full h-11 rounded-lg bg-violet-600 hover:bg-violet-700 text-white text-sm font-semibold">Simpan PIN</button>
          </div>
        </div>
      )}
    </div>
  );
}

export function AdminAudit() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/audit-logs").then((r) => setItems(r.data)).catch(()=>{}); }, []);
  return (
    <div className="space-y-4">
      <h1 className="font-display text-2xl font-bold">Audit Log</h1>
      <div className="bg-white rounded-xl border border-slate-200 divide-y divide-slate-100">
        {items.map((a) => (
          <div key={a.id} className="p-3 text-sm flex items-start justify-between gap-3">
            <div>
              <div className="font-semibold">{a.action} <span className="text-slate-500">•</span> <span className="text-slate-500">{a.module}</span></div>
              <div className="text-xs text-slate-500">{a.user_email} ({a.user_role})</div>
            </div>
            <div className="text-xs text-slate-500 whitespace-nowrap">{formatDateID(a.created_at)}</div>
          </div>
        ))}
        {items.length === 0 && <div className="p-6 text-center text-sm text-slate-500">Belum ada aktivitas.</div>}
      </div>
    </div>
  );
}

const PROMO_STATUS_LABEL = { berjalan: "Sedang Berjalan", akan_datang: "Akan Datang", berakhir: "Telah Berakhir", nonaktif: "Nonaktif" };
const PROMO_STATUS_CLS = { berjalan: "bg-emerald-100 text-emerald-700", akan_datang: "bg-amber-100 text-amber-700", berakhir: "bg-slate-100 text-slate-500", nonaktif: "bg-red-100 text-red-600" };

function PromoPanel() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [pq, setPq] = useState("");
  const load = () => {
    api.get("/admin/promos").then((r) => setItems(r.data)).catch(() => {});
    api.get("/admin/promos/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(() => { load(); api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const blank = { name: "", type: "promo_toko", product_ids: [], discount_percent: 10, bundle_min_qty: 2, tiers: [{ min_qty: 10, percent: 10 }], start_at: "", end_at: "", active: true };
  const openCreate = (type) => { setForm({ ...blank, type, id: null, tiers: [{ min_qty: 10, percent: 10 }] }); setPq(""); };
  const openEdit = (p) => setForm({ id: p.id, name: p.name, type: p.type, product_ids: p.product_ids || [], discount_percent: p.discount_percent, bundle_min_qty: p.bundle_min_qty || 2, tiers: (p.tiers && p.tiers.length ? p.tiers : [{ min_qty: 10, percent: 10 }]), start_at: (p.start_at || "").slice(0, 16), end_at: (p.end_at || "").slice(0, 16), active: p.active !== false });
  const duplicate = (p) => setForm({ ...blank, id: null, name: p.name + " (Salinan)", type: p.type, product_ids: p.product_ids || [], discount_percent: p.discount_percent, bundle_min_qty: p.bundle_min_qty || 2, start_at: "", end_at: "" });

  const save = async () => {
    if (!form.name.trim()) return setMsg("Isi nama promo");
    if (!form.product_ids.length) return setMsg("Pilih minimal 1 produk");
    if (form.type === "grosir" && !(form.tiers || []).length) return setMsg("Isi minimal 1 jenjang grosir");
    try {
      const payload = { ...form, discount_percent: +form.discount_percent, bundle_min_qty: +form.bundle_min_qty };
      delete payload.id;
      if (form.id) await api.patch(`/admin/promos/${form.id}`, payload);
      else await api.post("/admin/promos", payload);
      setMsg("Promo tersimpan");
      setForm(null);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan promo"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus promo ini?")) return;
    await api.delete(`/admin/promos/${id}`).catch(() => {});
    load();
  };

  const toggleProduct = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  const filteredProducts = products.filter((p) => !pq || p.name.toLowerCase().includes(pq.toLowerCase()));

  return (
    <div className="space-y-4">
      {/* Buat Diskon */}
      <div className="grid md:grid-cols-3 gap-3">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="font-semibold text-sm">Promo Toko</div>
          <p className="text-xs text-slate-500 mt-1 mb-3">Buat diskon untuk produk yang kamu pilih — harga coret & badge PROMO tampil otomatis di toko.</p>
          <button data-testid="promo-new-toko" onClick={() => openCreate("promo_toko")} className="h-9 px-5 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold">Buat</button>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="font-semibold text-sm">Paket Diskon (Bundle)</div>
          <p className="text-xs text-slate-500 mt-1 mb-3">Beli N pcs atau lebih pada produk terpilih, dapat diskon tambahan per pcs.</p>
          <button data-testid="promo-new-bundle" onClick={() => openCreate("bundle")} className="h-9 px-5 rounded-lg bg-kiyana-maroon text-white text-xs font-semibold">Buat</button>
        </div>
        <div className="bg-white rounded-xl border-2 border-emerald-200 p-4">
          <div className="font-semibold text-sm flex items-center gap-1.5">Harga Grosir <span className="text-[9px] bg-emerald-600 text-white rounded px-1.5 py-0.5 font-bold">GROSIR</span></div>
          <p className="text-xs text-slate-500 mt-1 mb-3">Jenjang harga pembelian banyak (mis. 10+ -10%, 25+ -15%) — badge GROSIR tampil di kartu produk.</p>
          <button data-testid="promo-new-grosir" onClick={() => openCreate("grosir")} className="h-9 px-5 rounded-lg bg-emerald-600 text-white text-xs font-semibold">Buat</button>
        </div>
      </div>

      {/* Form */}
      {form && (
        <div data-testid="promo-form" className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm">{form.id ? "Ubah" : "Buat"} {form.type === "bundle" ? "Paket Diskon (Bundle)" : form.type === "grosir" ? "Harga Grosir" : "Promo Toko"}</div>
            <button onClick={() => setForm(null)} className="text-xs text-slate-500 border border-slate-200 rounded-lg px-2.5 py-1">Batal</button>
          </div>
          <div className="grid md:grid-cols-4 gap-3">
            <input data-testid="promo-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama promo (mis. Promo September)" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            {form.type !== "grosir" && (
              <div>
                <label className="text-[10px] text-slate-500">Diskon (%)</label>
                <input data-testid="promo-discount" type="number" min="1" max="90" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            {form.type === "grosir" && (
              <div className="md:col-span-2">
                <label className="text-[10px] text-slate-500">Jenjang grosir (min pcs → diskon %)</label>
                {(form.tiers || []).map((t, i) => (
                  <div key={i} className="flex gap-2 items-center mt-1.5">
                    <input data-testid={`tier-min-${i}`} type="number" min="2" value={t.min_qty} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, min_qty: e.target.value } : x) })} placeholder="Min pcs" className="w-28 h-9 px-2.5 rounded-lg border border-slate-200 text-xs" />
                    <input data-testid={`tier-pct-${i}`} type="number" min="1" max="90" value={t.percent} onChange={(e) => setForm({ ...form, tiers: form.tiers.map((x, j) => j === i ? { ...x, percent: e.target.value } : x) })} placeholder="Diskon %" className="w-28 h-9 px-2.5 rounded-lg border border-slate-200 text-xs" />
                    <button type="button" data-testid={`tier-del-${i}`} onClick={() => setForm({ ...form, tiers: form.tiers.filter((_, j) => j !== i) })} className="text-red-400 hover:text-red-600"><X className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
                <button type="button" data-testid="tier-add" onClick={() => setForm({ ...form, tiers: [...(form.tiers || []), { min_qty: ((form.tiers || []).at(-1)?.min_qty || 0) + 10, percent: 10 }] })} className="mt-1.5 h-8 px-3 rounded-lg border border-dashed border-slate-300 text-[11px] text-slate-600 flex items-center gap-1"><Plus className="w-3 h-3" /> Tambah Jenjang</button>
              </div>
            )}
            {form.type === "bundle" && (
              <div>
                <label className="text-[10px] text-slate-500">Minimal beli (pcs)</label>
                <input data-testid="promo-bundle-min" type="number" min="2" value={form.bundle_min_qty} onChange={(e) => setForm({ ...form, bundle_min_qty: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
              </div>
            )}
            <div>
              <label className="text-[10px] text-slate-500">Mulai</label>
              <input data-testid="promo-start" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Berakhir</label>
              <input data-testid="promo-end" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] text-slate-500">Pilih produk ({form.product_ids.length} dipilih)</label>
              <input data-testid="promo-product-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk..." className="h-8 px-2.5 rounded-lg border border-slate-200 text-xs w-44" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto border border-slate-100 rounded-lg p-2">
              {filteredProducts.map((p) => {
                const on = form.product_ids.includes(p.id);
                return (
                  <label key={p.id} data-testid={`promo-product-${p.id}`} className={`flex items-center gap-2 rounded-lg border p-1.5 cursor-pointer text-xs ${on ? "border-kiyana-maroon bg-pink-50" : "border-slate-200"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleProduct(p.id)} className="accent-pink-700" />
                    <img src={p.image} alt="" className="w-8 h-8 rounded object-cover" />
                    <span className="line-clamp-2 leading-tight">{p.name}<span className="block text-[10px] text-slate-400">{formatIDR(p.price)}</span></span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs"><input data-testid="promo-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-pink-700" /> Aktif</label>
            <button data-testid="promo-save" onClick={save} className="h-10 px-6 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold">{form.id ? "Simpan Perubahan" : "Buat Promo"}</button>
          </div>
        </div>
      )}

      {/* Performa */}
      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="text-sm font-semibold mb-3">Performa Promosi <span className="text-[10px] text-slate-400 font-normal">(7 hari terakhir, produk dalam promo berjalan)</span></div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {[
            { label: "Penjualan", value: stats ? formatIDR(stats.penjualan) : "…", testid: "promo-stat-sales" },
            { label: "Pesanan", value: stats?.pesanan ?? "…", testid: "promo-stat-orders" },
            { label: "Jumlah Terjual", value: stats?.terjual ?? "…", testid: "promo-stat-sold" },
            { label: "Pembeli", value: stats?.pembeli ?? "…", testid: "promo-stat-buyers" },
          ].map((c) => (
            <div key={c.label} data-testid={c.testid} className="bg-slate-50 rounded-lg p-3">
              <div className="text-[10px] uppercase tracking-widest text-slate-500">{c.label}</div>
              <div className="text-lg font-display font-bold mt-0.5">{c.value}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Daftar Promosi */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left p-3">Promo</th><th className="text-left p-3">Tipe</th><th className="text-left p-3">Produk</th><th className="text-left p-3">Diskon</th><th className="text-left p-3">Periode</th><th className="text-left p-3">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-slate-400">Belum ada promo. Klik "Buat" di atas untuk membuat promo pertama.</td></tr>}
            {items.map((p) => (
              <tr key={p.id} data-testid={`promo-row-${p.id}`}>
                <td className="p-3">
                  <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${PROMO_STATUS_CLS[p.status]}`} data-testid={`promo-status-${p.id}`}>{PROMO_STATUS_LABEL[p.status]}</span>
                  <div className="font-semibold text-xs mt-1">{p.name}</div>
                </td>
                <td className="p-3 text-xs">{p.type === "bundle" ? "Paket Diskon" : p.type === "grosir" ? "Harga Grosir" : "Promo Toko"}</td>
                <td className="p-3">
                  <div className="flex items-center">
                    {(p.products || []).slice(0, 4).map((pr) => <img key={pr.id} src={pr.image} alt={pr.name} title={pr.name} className="w-7 h-7 rounded-full border-2 border-white object-cover -ml-1.5 first:ml-0" />)}
                    {(p.products || []).length > 4 && <span className="text-[10px] text-slate-500 ml-1">+{p.products.length - 4}</span>}
                  </div>
                </td>
                <td className="p-3 text-xs font-bold text-kiyana-maroon">
                  {p.type === "grosir"
                    ? (p.tiers || []).map((t) => `${t.min_qty}+: -${t.percent}%`).join(" • ")
                    : `-${p.discount_percent}%${p.type === "bundle" ? ` (min ${p.bundle_min_qty} pcs)` : ""}`}
                </td>
                <td className="p-3 text-[11px] text-slate-500 whitespace-nowrap">{p.start_at ? formatDateID(p.start_at) : "Sekarang"}<br />— {p.end_at ? formatDateID(p.end_at) : "Tanpa batas"}</td>
                <td className="p-3">
                  <div className="flex flex-col gap-1 items-start">
                    <button data-testid={`promo-edit-${p.id}`} onClick={() => openEdit(p)} className="text-xs text-kiyana-maroon font-medium">Ubah</button>
                    <button data-testid={`promo-dup-${p.id}`} onClick={() => duplicate(p)} className="text-xs text-slate-500">Duplikat</button>
                    <button data-testid={`promo-del-${p.id}`} onClick={() => remove(p.id)} className="text-xs text-red-500">Hapus</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function FlashSalePanel() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(null);
  const [msg, setMsg] = useState("");
  const [pq, setPq] = useState("");
  const load = () => api.get("/admin/flashsales").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); api.get("/products", { params: { limit: 200 } }).then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const blank = { name: "", product_ids: [], discount_percent: 50, quota: 20, sold_boost: 0, start_at: "", end_at: "", active: true };
  const openEdit = (f) => setForm({ id: f.id, name: f.name, product_ids: f.product_ids || [], discount_percent: f.discount_percent, quota: f.quota || 20, sold_boost: f.sold_boost || 0, start_at: (f.start_at || "").slice(0, 16), end_at: (f.end_at || "").slice(0, 16), active: f.active !== false });

  const save = async () => {
    if (!form.name.trim()) return setMsg("Isi nama flash sale");
    if (!form.product_ids.length) return setMsg("Pilih minimal 1 produk");
    if (!form.end_at) return setMsg("Isi waktu berakhir (untuk countdown)");
    try {
      const payload = { ...form, discount_percent: +form.discount_percent, quota: +form.quota, sold_boost: +form.sold_boost };
      delete payload.id;
      if (form.id) await api.patch(`/admin/flashsales/${form.id}`, payload);
      else await api.post("/admin/flashsales", payload);
      setMsg("Flash sale tersimpan");
      setForm(null);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || "Gagal menyimpan flash sale"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus flash sale ini?")) return;
    await api.delete(`/admin/flashsales/${id}`).catch(() => {});
    load();
  };

  const toggleProduct = (id) => setForm((f) => ({ ...f, product_ids: f.product_ids.includes(id) ? f.product_ids.filter((x) => x !== id) : [...f.product_ids, id] }));
  const filteredProducts = products.filter((p) => !pq || p.name.toLowerCase().includes(pq.toLowerCase()));

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between gap-3">
        <div>
          <div className="font-semibold text-sm">Flash Sale</div>
          <p className="text-xs text-slate-500 mt-1">Tampil di Beranda dengan countdown, diskon besar, dan progress "Stok Terbatas". Angka <b>Tampilan Terjual</b> bisa kamu atur agar terlihat laris (ditambah penjualan asli selama periode).</p>
        </div>
        <button data-testid="flash-new-btn" onClick={() => { setForm({ ...blank, id: null }); setPq(""); }} className="h-10 px-4 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold flex items-center gap-2 flex-shrink-0">
          <Plus className="w-4 h-4" /> Flash Sale Baru
        </button>
      </div>

      {msg && <div className="text-xs text-kiyana-maroon" data-testid="flash-msg">{msg}</div>}

      {form && (
        <div data-testid="flash-form" className="bg-white rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div className="font-semibold text-sm">{form.id ? "Ubah" : "Buat"} Flash Sale</div>
            <button onClick={() => setForm(null)} className="text-xs text-slate-500 border border-slate-200 rounded-lg px-2.5 py-1">Batal</button>
          </div>
          <div className="grid md:grid-cols-3 gap-3">
            <input data-testid="flash-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Nama (mis. Flash Sale 9.9)" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            <div>
              <label className="text-[10px] text-slate-500">Diskon (%)</label>
              <input data-testid="flash-discount" type="number" min="1" max="95" value={form.discount_percent} onChange={(e) => setForm({ ...form, discount_percent: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Kuota stok flash / produk</label>
              <input data-testid="flash-quota" type="number" min="1" value={form.quota} onChange={(e) => setForm({ ...form, quota: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Tampilan "terjual" awal (atur agar menarik)</label>
              <input data-testid="flash-sold-boost" type="number" min="0" value={form.sold_boost} onChange={(e) => setForm({ ...form, sold_boost: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Mulai (WIB)</label>
              <input data-testid="flash-start" type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500">Berakhir (WIB) — countdown</label>
              <input data-testid="flash-end" type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} className="w-full h-10 px-3 rounded-lg border border-slate-200 text-sm" />
            </div>
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-[10px] text-slate-500">Pilih produk ({form.product_ids.length} dipilih)</label>
              <input data-testid="flash-product-search" value={pq} onChange={(e) => setPq(e.target.value)} placeholder="Cari produk..." className="h-8 px-2.5 rounded-lg border border-slate-200 text-xs w-44" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5 max-h-56 overflow-y-auto border border-slate-100 rounded-lg p-2">
              {filteredProducts.map((p) => {
                const on = form.product_ids.includes(p.id);
                return (
                  <label key={p.id} data-testid={`flash-product-${p.id}`} className={`flex items-center gap-2 rounded-lg border p-1.5 cursor-pointer text-xs ${on ? "border-kiyana-maroon bg-pink-50" : "border-slate-200"}`}>
                    <input type="checkbox" checked={on} onChange={() => toggleProduct(p.id)} className="accent-pink-700" />
                    <img src={p.image} alt="" className="w-8 h-8 rounded object-cover" />
                    <span className="line-clamp-2 leading-tight">{p.name}<span className="block text-[10px] text-slate-400">{formatIDR(p.price)}</span></span>
                  </label>
                );
              })}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 text-xs"><input data-testid="flash-active" type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} className="accent-pink-700" /> Aktif</label>
            <button data-testid="flash-save" onClick={save} className="h-10 px-6 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold">{form.id ? "Simpan Perubahan" : "Buat Flash Sale"}</button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr><th className="text-left p-3">Flash Sale</th><th className="text-left p-3">Produk</th><th className="text-left p-3">Diskon</th><th className="text-left p-3">Kuota</th><th className="text-left p-3">Periode</th><th className="text-left p-3">Aksi</th></tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-sm text-slate-400">Belum ada flash sale.</td></tr>}
            {items.map((f) => (
              <tr key={f.id} data-testid={`flash-row-${f.id}`}>
                <td className="p-3">
                  <span className={`text-[10px] font-semibold rounded px-1.5 py-0.5 ${PROMO_STATUS_CLS[f.status]}`}>{PROMO_STATUS_LABEL[f.status]}</span>
                  <div className="font-semibold text-xs mt-1">{f.name}</div>
                </td>
                <td className="p-3">
                  <div className="flex items-center">
                    {(f.products || []).slice(0, 4).map((pr) => <img key={pr.id} src={pr.image} alt={pr.name} title={pr.name} className="w-7 h-7 rounded-full border-2 border-white object-cover -ml-1.5 first:ml-0" />)}
                    {(f.products || []).length > 4 && <span className="text-[10px] text-slate-500 ml-1">+{f.products.length - 4}</span>}
                  </div>
                </td>
                <td className="p-3 text-xs font-bold text-kiyana-maroon">-{f.discount_percent}%</td>
                <td className="p-3 text-xs">{f.quota}/produk<br /><span className="text-[10px] text-slate-400">tampilan terjual +{f.sold_boost || 0}</span></td>
                <td className="p-3 text-[11px] text-slate-500 whitespace-nowrap">{f.start_at ? formatDateID(f.start_at) : "Sekarang"}<br />— {f.end_at ? formatDateID(f.end_at) : "-"}</td>
                <td className="p-3">
                  <div className="flex flex-col gap-1 items-start">
                    <button data-testid={`flash-edit-${f.id}`} onClick={() => openEdit(f)} className="text-xs text-kiyana-maroon font-medium">Ubah</button>
                    <button data-testid={`flash-del-${f.id}`} onClick={() => remove(f.id)} className="text-xs text-red-500">Hapus</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminVouchers() {
  const [tab, setTab] = useState("voucher");
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-display text-2xl font-bold">Voucher & Promo</h1>
          <p className="text-sm text-slate-500">Kelola voucher diskon, promo produk terpilih, dan flash sale.</p>
        </div>
        <div className="flex gap-1 bg-white border border-slate-200 rounded-full p-1">
          {[["voucher", "Voucher"], ["promo", "Promo Produk"], ["flash", "Flash Sale"]].map(([id, label]) => (
            <button key={id} data-testid={`vp-tab-${id}`} onClick={() => setTab(id)}
              className={`px-4 h-8 rounded-full text-xs font-medium transition ${tab === id ? "bg-kiyana-maroon text-white" : "text-slate-600 hover:bg-slate-50"}`}>{label}</button>
          ))}
        </div>
      </div>
      {tab === "voucher" ? <VoucherPanel /> : tab === "promo" ? <PromoPanel /> : <FlashSalePanel />}
    </div>
  );
}

function VoucherPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ code: "", title: "", percent: 10, min_purchase: 0, max_discount: 50000 });
  const [showForm, setShowForm] = useState(false);
  const load = () => api.get("/admin/vouchers").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    try {
      await api.post("/admin/vouchers", form);
      setShowForm(false);
      setForm({ code: "", title: "", percent: 10, min_purchase: 0, max_discount: 50000 });
      load();
    } catch (err) {
      alert(err?.response?.data?.detail || "Gagal membuat voucher");
    }
  };

  const toggle = async (id) => {
    await api.patch(`/admin/vouchers/${id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold">Voucher & Promo</h1>
          <p className="text-sm text-slate-500">Voucher diskon tampil di Web Store dan bisa dipakai saat checkout.</p>
        </div>
        <button data-testid="voucher-new-btn" onClick={() => setShowForm(!showForm)} className="h-10 px-4 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold flex items-center gap-2">
          <Plus className="w-4 h-4" /> Voucher Baru
        </button>
      </div>

      {showForm && (
        <form onSubmit={create} className="bg-white rounded-xl border border-slate-200 p-4 grid md:grid-cols-5 gap-3">
          <input data-testid="voucher-code-input" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="KODE" required className="h-10 px-3 rounded-lg border border-slate-200 text-sm font-mono" />
          <input data-testid="voucher-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Judul promo" required className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <input data-testid="voucher-percent-input" type="number" min="1" max="90" value={form.percent} onChange={(e) => setForm({ ...form, percent: +e.target.value })} placeholder="% diskon" required className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <input data-testid="voucher-min-input" type="number" min="0" step="1000" value={form.min_purchase} onChange={(e) => setForm({ ...form, min_purchase: +e.target.value })} placeholder="Min. belanja" className="h-10 px-3 rounded-lg border border-slate-200 text-sm" />
          <button data-testid="voucher-save-btn" type="submit" className="h-10 rounded-lg bg-slate-900 text-white text-sm font-semibold">Simpan</button>
        </form>
      )}

      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase text-slate-500">
            <tr>
              <th className="text-left p-3">Kode</th>
              <th className="text-left p-3">Judul</th>
              <th className="text-right p-3">Diskon</th>
              <th className="text-right p-3">Min. Belanja</th>
              <th className="text-right p-3">Maks. Potongan</th>
              <th className="text-left p-3">Status</th>
              <th className="p-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {items.map((v) => (
              <tr key={v.id} data-testid={`voucher-row-${v.code}`}>
                <td className="p-3 font-mono font-bold">{v.code}</td>
                <td className="p-3">{v.title}</td>
                <td className="p-3 text-right">{v.percent}%</td>
                <td className="p-3 text-right">{formatIDR(v.min_purchase)}</td>
                <td className="p-3 text-right">{formatIDR(v.max_discount)}</td>
                <td className="p-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${v.active ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                    {v.active ? "Aktif" : "Nonaktif"}
                  </span>
                </td>
                <td className="p-3">
                  <button data-testid={`voucher-toggle-${v.code}`} onClick={() => toggle(v.id)} className="text-xs text-slate-500 hover:text-kiyana-maroon flex items-center gap-1">
                    <Power className="w-3.5 h-3.5" /> {v.active ? "Nonaktifkan" : "Aktifkan"}
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={7} className="p-8 text-center text-slate-500">Belum ada voucher.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function AdminSettings() {
  const [s, setS] = useState(null);
  const [smtpPassword, setSmtpPassword] = useState("");
  const [waToken, setWaToken] = useState("");
  const [biteshipKey, setBiteshipKey] = useState("");
  const [testTo, setTestTo] = useState("");
  const [cats, setCats] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/settings").then((r) => { setS(r.data); setCats(r.data.store_cats || []); }).catch(() => setS(false));
  useEffect(() => { load(); }, []);

  if (s === null) return <div className="text-sm text-slate-500">Memuat pengaturan...</div>;
  if (s === false) return <div className="text-sm text-red-500">Akses ditolak — hanya Owner yang dapat mengubah pengaturan.</div>;

  const save = async (e) => {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      smtp_host: fd.get("smtp_host"), smtp_port: +fd.get("smtp_port"), smtp_user: fd.get("smtp_user"),
      smtp_from_name: fd.get("smtp_from_name"), wa_mode: fd.get("wa_mode"), wa_gateway_url: fd.get("wa_gateway_url"),
      cod_enabled: fd.get("cod_enabled") === "on",
      cod_fee_percent: parseFloat(fd.get("cod_fee_percent")) || 0,
      cod_max_total: parseInt(fd.get("cod_max_total")) || 0,
      pos_lock_minutes: parseInt(fd.get("pos_lock_minutes")) || 10,
      store_cats: cats.filter((c) => c.name?.trim() && c.id?.trim()),
    };
    if (smtpPassword) payload.smtp_password = smtpPassword;
    if (waToken) payload.wa_gateway_token = waToken;
    if (biteshipKey) payload.biteship_key = biteshipKey;
    try {
      await api.put("/admin/settings", payload);
      setMsg("Pengaturan tersimpan");
      setSmtpPassword(""); setWaToken(""); setBiteshipKey("");
      load();
    } catch (err) { setMsg(err?.response?.data?.detail || "Gagal menyimpan"); }
  };

  const testEmail = async () => {
    if (!testTo) return setMsg("Isi email tujuan test dulu");
    try {
      await api.post("/admin/settings/test-email", { to: testTo });
      setMsg("Email test terkirim ke " + testTo);
    } catch (err) { setMsg(err?.response?.data?.detail || "Gagal kirim test email"); }
  };

  const inputCls = "w-full h-10 px-3 rounded-lg border border-slate-200 text-sm";

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="font-display text-2xl font-bold">Pengaturan</h1>
        <p className="text-sm text-slate-500">Konfigurasi SMTP Gmail untuk OTP email dan gateway WhatsApp. Secret tersimpan aman di server, tidak pernah dikirim ke frontend.</p>
      </div>

      <form onSubmit={save} className="space-y-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">SMTP Email (Gmail)</div>
          <p className="text-xs text-slate-500 mb-4">Gunakan Gmail + App Password (16 karakter) dari myaccount.google.com → Security → 2-Step Verification → App passwords.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">SMTP Host</label>
              <input name="smtp_host" data-testid="smtp-host" defaultValue={s.smtp_host} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Port</label>
              <input name="smtp_port" data-testid="smtp-port" type="number" defaultValue={s.smtp_port} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Email Gmail pengirim</label>
              <input name="smtp_user" data-testid="smtp-user" defaultValue={s.smtp_user} placeholder="officialcireboncloth@gmail.com" className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Nama pengirim</label>
              <input name="smtp_from_name" data-testid="smtp-from-name" defaultValue={s.smtp_from_name} className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">App Password {s.smtp_password_set && <span className="text-emerald-600">(sudah tersimpan — kosongkan jika tidak diubah)</span>}</label>
              <input data-testid="smtp-password" type="password" value={smtpPassword} onChange={(e) => setSmtpPassword(e.target.value)} placeholder="xxxx xxxx xxxx xxxx" className={inputCls} />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <input data-testid="test-email-to" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Email tujuan test" className={inputCls + " max-w-xs"} />
            <button type="button" data-testid="test-email-btn" onClick={testEmail} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Kirim Test Email</button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">WhatsApp OTP</div>
          <p className="text-xs text-slate-500 mb-4">Mode <b>Mock</b>: kode OTP tampil di layar (testing). Mode <b>Gateway</b>: kirim via WA gateway (mis. WA personal yang di-scan QR seperti WA Web) — isi URL endpoint dan token gateway kamu.</p>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500">Mode</label>
              <select name="wa_mode" data-testid="wa-mode" defaultValue={s.wa_mode} className={inputCls}>
                <option value="mock">Mock (testing, OTP tampil di layar)</option>
                <option value="gateway">Gateway (WA scan QR)</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500">Gateway URL</label>
              <input name="wa_gateway_url" data-testid="wa-url" defaultValue={s.wa_gateway_url} placeholder="https://gateway-wa-kamu/send" className={inputCls} />
            </div>
            <div className="md:col-span-2">
              <label className="text-xs text-slate-500">Gateway Token {s.wa_gateway_token_set && <span className="text-emerald-600">(sudah tersimpan — kosongkan jika tidak diubah)</span>}</label>
              <input data-testid="wa-token" type="password" value={waToken} onChange={(e) => setWaToken(e.target.value)} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">Ekspedisi — Biteship (JNE & SPX)</div>
          <p className="text-xs text-slate-500 mb-4">
            Origin pengiriman: <b>Kota Cirebon (45111)</b>. Tanpa API key, ongkir & tracking berjalan mode simulasi realistis.
            Dapatkan key di dashboard Biteship → Integrations → API Keys (gunakan key production saat live).
          </p>
          <label className="text-xs text-slate-500">Biteship API Key {s.biteship_key_set && <span className="text-emerald-600">(sudah tersimpan — kosongkan jika tidak diubah)</span>}</label>
          <input data-testid="biteship-key" type="password" value={biteshipKey} onChange={(e) => setBiteshipKey(e.target.value)} placeholder="biteship_live_..." className={inputCls} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">POS & Keamanan Kasir</div>
          <p className="text-xs text-slate-500 mb-4">Setelah tidak ada aktivitas selama durasi ini, layar kasir otomatis terkunci dan petugas wajib autentikasi ulang via PIN / ID Card. Auto Lock tidak menutup cash drawer maupun shift.</p>
          <label className="text-xs text-slate-500">Auto Lock POS (menit)</label>
          <input name="pos_lock_minutes" data-testid="pos-lock-minutes" type="number" min="1" max="120" defaultValue={s.pos_lock_minutes} className={inputCls + " max-w-[160px]"} />
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">Menu Kategori Beranda (Web Store)</div>
          <p className="text-xs text-slate-500 mb-4">Atur barisan ikon menu di Beranda toko: nama, ikon, dan target kategori (slug: <code>all</code>, <code>atasan</code>, <code>bottom</code>, <code>dress</code>, <code>setelan</code>, <code>aksesori</code>).</p>
          <div className="space-y-2">
            {cats.map((c, i) => (
              <div key={i} className="flex gap-2 items-center" data-testid={`cat-row-${i}`}>
                <select data-testid={`cat-icon-${i}`} value={c.icon} onChange={(e) => setCats(cats.map((x, j) => j === i ? { ...x, icon: e.target.value } : x))} className={inputCls + " w-36"}>
                  {["layout-grid", "shirt", "layers", "sparkles", "copy", "shopping-bag", "star", "crown", "gem", "tag", "gift"].map((o) => <option key={o} value={o}>{o}</option>)}
                </select>
                <input data-testid={`cat-name-${i}`} value={c.name} onChange={(e) => setCats(cats.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Nama menu" className={inputCls} />
                <input data-testid={`cat-slug-${i}`} value={c.id} onChange={(e) => setCats(cats.map((x, j) => j === i ? { ...x, id: e.target.value } : x))} placeholder="Slug kategori" className={inputCls + " w-40"} />
                <button type="button" data-testid={`cat-del-${i}`} onClick={() => setCats(cats.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 flex-shrink-0"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" data-testid="cat-add" onClick={() => setCats([...cats, { id: "", name: "", icon: "star" }])} className="h-9 px-4 rounded-lg border border-dashed border-slate-300 text-xs text-slate-600 flex items-center gap-1.5">
              <Plus className="w-3.5 h-3.5" /> Tambah Menu
            </button>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="font-semibold mb-1">COD (Bayar di Tempat)</div>
          <p className="text-xs text-slate-500 mb-4">Aktifkan metode COD dengan fee persentase dari total belanja. Pelanggan COD wajib mengisi formulir perjanjian + foto KTP/Paspor, dan pesanan baru diproses setelah admin verifikasi.</p>
          <div className="grid md:grid-cols-3 gap-3 items-end">
            <label className="flex items-center gap-2 h-10">
              <input name="cod_enabled" data-testid="cod-enabled" type="checkbox" defaultChecked={s.cod_enabled} className="w-4 h-4 accent-pink-700" />
              <span className="text-sm">Aktifkan COD</span>
            </label>
            <div>
              <label className="text-xs text-slate-500">Fee COD (% dari total belanja)</label>
              <input name="cod_fee_percent" data-testid="cod-fee-percent" type="number" step="0.1" min="0" max="100" defaultValue={s.cod_fee_percent} className={inputCls} />
            </div>
            <div>
              <label className="text-xs text-slate-500">Maks. total belanja COD (Rp)</label>
              <input name="cod_max_total" data-testid="cod-max-total" type="number" min="0" step="10000" defaultValue={s.cod_max_total} className={inputCls} />
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button data-testid="settings-save-btn" type="submit" className="h-11 px-6 rounded-lg bg-kiyana-maroon text-white text-sm font-semibold">Simpan Pengaturan</button>
          {msg && <span className="text-sm text-slate-600" data-testid="settings-msg">{msg}</span>}
        </div>
      </form>
    </div>
  );
}

export function AdminPayments() {
  const [proofs, setProofs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [rejectId, setRejectId] = useState(null);
  const [reason, setReason] = useState("");
  const [msg, setMsg] = useState("");

  const load = () => {
    api.get("/admin/payments/manual").then((r) => setProofs(r.data)).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const orderOf = (id) => orders.find((o) => o.id === id);

  const act = async (id, action) => {
    try {
      await api.patch(`/admin/payments/manual/${id}`, { action, reason });
      setMsg(action === "approve" ? "Pembayaran diterima" : "Bukti ditolak");
      setRejectId(null);
      setReason("");
      load();
    } catch (e) {
      setMsg(e?.response?.data?.detail || "Gagal memproses");
    }
  };

  const badge = (s) => s === "diterima" ? "bg-emerald-100 text-emerald-700" : s === "ditolak" ? "bg-red-100 text-red-600" : "bg-amber-100 text-amber-700";

  return (
    <div className="space-y-4">
      <div>
        <h1 className="font-display text-2xl font-bold">Pembayaran Manual</h1>
        <p className="text-sm text-slate-500">Verifikasi bukti transfer/QRIS pelanggan. Penolakan wajib disertai alasan.</p>
        {msg && <div className="text-xs text-kiyana-maroon mt-1" data-testid="payments-msg">{msg}</div>}
      </div>

      <div className="space-y-3">
        {proofs.length === 0 && <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-500">Belum ada bukti pembayaran masuk.</div>}
        {proofs.map((p) => {
          const o = orderOf(p.order_id);
          return (
            <div key={p.id} data-testid={`proof-${p.id}`} className="bg-white rounded-xl border border-slate-200 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-semibold text-sm">#{o?.code || p.order_id.slice(0, 8)} <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${badge(p.status)}`}>{p.status.replace(/_/g, " ")}</span></div>
                  <div className="text-xs text-slate-500 mt-1">{p.sender_name} • {p.bank_name} • Bayar {formatDateID(p.paid_at)}</div>
                  <div className="text-sm font-bold text-kiyana-maroon mt-1">{formatIDR(p.amount)}</div>
                  {p.status === "ditolak" && p.rejection_reason && <div className="text-xs text-red-500 mt-1">Alasan: {p.rejection_reason}</div>}
                </div>
                <div className="flex items-center gap-2">
                  {p.proof_url && (
                    <a data-testid={`proof-view-${p.id}`} href={`${process.env.REACT_APP_BACKEND_URL}${p.proof_url}`} target="_blank" rel="noreferrer"
                      className="h-9 px-3 rounded-lg border border-slate-200 text-xs flex items-center">Lihat Bukti</a>
                  )}
                  {p.status === "menunggu_verifikasi" && (
                    <>
                      <button data-testid={`proof-approve-${p.id}`} onClick={() => act(p.id, "approve")}
                        className="h-9 px-4 rounded-lg bg-emerald-600 text-white text-xs font-semibold flex items-center gap-1">
                        <Check className="w-3.5 h-3.5" /> Terima
                      </button>
                      <button data-testid={`proof-reject-${p.id}`} onClick={() => setRejectId(rejectId === p.id ? null : p.id)}
                        className="h-9 px-4 rounded-lg bg-red-50 text-red-600 text-xs font-semibold border border-red-200">Tolak</button>
                    </>
                  )}
                </div>
              </div>
              {rejectId === p.id && (
                <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                  <input data-testid="reject-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan penolakan (wajib)" className="flex-1 h-10 px-3 rounded-lg border border-slate-200 text-sm" />
                  <button data-testid="reject-confirm" onClick={() => act(p.id, "reject")} className="h-10 px-4 rounded-lg bg-red-600 text-white text-xs font-semibold">Tolak Bukti</button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function AdminPlaceholder({ title, desc }) {
  return (
    <div className="max-w-lg">
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
        <div className="w-14 h-14 rounded-xl bg-kiyana-soft mx-auto flex items-center justify-center mb-4">
          <ChevronRight className="w-6 h-6 text-kiyana-maroon" />
        </div>
        <h1 className="font-display text-2xl font-bold mb-2">{title}</h1>
        <p className="text-sm text-slate-500">{desc || "Modul ini menjadi bagian roadmap Tahap 2. Fondasi database dan navigasi sudah tersedia."}</p>
      </div>
    </div>
  );
}
